import { SortablejsOptions } from "angular-sortablejs";
import {
  AdminApi,
  Emoji,
  EmojiApi,
  Game,
  GameCategory,
  Prize,
} from "@app/shared/sdk";
import { fuseAnimations } from "@fuse/animations";
import * as moment from "moment";
import * as shuffle from "shuffle-array";
import * as _ from "lodash";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { ConfirmationDialogComponent } from "@app/main/content/confirmation-dialog/confirmation-dialog.component";
import { GameSettingsService } from "@app/main/content/game-settings/game-settings.service";
import { ActivatedRoute, Router } from "@angular/router";
import { FormArray, FormBuilder, FormGroup, Validators, FormControl } from "@angular/forms";
import { GeneralSettingsService } from "@app/main/content/general-settings/general-settings.service";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MatDialog, MatDialogRef, MatSnackBar } from "@angular/material";
import { PrizeFormComponent } from "@app/main/content/game-settings/prize-form/prize-form.component";
import { PlayerListingComponent } from "@app/main/content/game-settings/player-listing/player-listing.component";
import { environment } from "@env/environment";
import { MatPaginator } from "@angular/material/paginator";
import { takeUntil } from "rxjs/operators";
import { Subject, Subscription } from "rxjs";
import { ExtraEmojisDatabase } from "./extra-emojis-database";
import { TitleCasePipe } from "@angular/common";
import { PrizeService } from "@app/main/core/services/prize.service";
import { HelperService } from "@app/main/core/services/helper.service";
import { prizeButtons } from "./prize-buttons";
import { DateValidationService } from "./services/date-validation.service";
import { MatchEditorComponent } from "./exact-match-editor/match-editor/match-editor.component";
import { GameSettingsStorageService } from "./game-settings-storage.service";
import {
  GameMenuShadowEffectOption,
  GameMenuEffectOption,
  GameEnterFreeOption,
} from "./shared/types/game-menu-effect-option.model";
import { ClubInvite } from "./reward-selector/reward-selector.component";
import { getPrizeImageSrc, getPrizeType, IMAGE_SLOTS, PLACEHOLDER_IMAGE } from "./prize-image.helper";
import {
  DressUpGroup,
  createEmptyDressUpGroups,
  normalizeDressUpGroups,
} from "./dress-up-config/dress-up-config.component";

@Component({
  selector: "app-game-settings",
  templateUrl: "./game-settings.component.html",
  styleUrls: ["./game-settings.component.scss"],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None,
})
export class GameSettingsComponent
  implements OnInit, DoCheck, AfterViewInit, OnDestroy {
  prizeButtons: any = prizeButtons;

  shadowOpt: GameMenuShadowEffectOption = {
    isActive: false,
    color: "#c4bfbf",
    blur: false,
  };
  categoryOpt: GameMenuEffectOption = {
    isActive: false,
    color: "#c4bfbf",
    showBackground: false,
  };
  titleOpt: GameMenuEffectOption = {
    isActive: false,
    color: "#c4bfbf",
    showBackground: false,
  };

  // FINAL X 2.3 — independent ENTER FREE toggle. Default `isActive: true`
  // matches the button's historical unconditional-show behaviour, so a
  // brand-new game looks the same as before unless the admin explicitly
  // hides it. Colours are the client's confirmed defaults (J W, 2026-08-03,
  // Asana 1217106461261286): "Colour 1: #62dd51 / Colour 2: #f5fb5f".
  enterFreeOpt: GameEnterFreeOption = {
    isActive: true,
    isBackgroundActive: true,
    firstColor: "#62dd51",
    secondColor: "#f5fb5f",
    isTextColorActive: false,
    textColor: "#000000",
  };

  // FINAL X p28-36, Seat Map format. Client ruling (J W, 2026-08-03, Asana
  // 1217106461261286): no fixed red/green hexes — "Use the gradient script",
  // i.e. the seat states are admin-configurable colour pairs rendered with
  // the same LinearGradient mechanism as ENTER FREE. Defaults reproduce the
  // spec's red-before / green-after so untouched games match the spec text.
  seatMapOpt = {
    unselectedFirstColor: "#e5544a",
    unselectedSecondColor: "#e5544a",
    selectedFirstColor: "#3ddc61",
    selectedSecondColor: "#3ddc61",
  };

  rules: any[] = [
    { value: "", viewValue: "None" },
    { value: "text", viewValue: "Text Details" },
    { value: "embed", viewValue: "Video Embedded Details" },
    { value: "image", viewValue: "Image Details" },
    { value: "embedWithText", viewValue: "Video Embed with Text" },
    { value: "imageWithText", viewValue: "Image with Text" },
  ];

  prizeTypes: any[] = [
    { value: "", viewValue: "None", disabled: false },
    { value: "auto select", viewValue: "AUTO", disabled: false },
    { value: "exact match", viewValue: "MATCH", disabled: false },
    { value: "solo", viewValue: "SOLO", disabled: false },
    { value: "top 6", viewValue: "TOP 6", disabled: false },
  ];

  manuArtSizeNavigation = "small";

  // Dummy data for GameAssetsSettingsComponent (header, prizeBackground, buttonBackground)
  dummyAssetsData: any = {
    header: {
      image: "",
      scale: 1
    },
    prizeBackground: {
      image: "",
      scale: 1
    },
    buttonBackground: {
      opacity: 0
    }
  };

  // Separate ticket data
  gameTicket: any = {
    image: "",
    scale: 1
  };

  // S3 bucket URL
  s3BucketUrl = environment.s3BucketUrl;

  prizes: Prize[] = [];

  form: FormGroup;

  // THE GAME OBJECT
  game: any;
  // game Logo
  gameLogo: any = null;
  gameLogoUrl: any;
  // current page number
  p = 1;

  prizePage = 1;

  menuArt: any = null;
  menuArtUrl: any;

  layer: any = null;
  layerUrl: any = null;

  automatchButtonArt: any = null;
  automatchButtonArtUrl: any;

  selfPickButtonArt: any = null;
  selfPickButtonArtUrl: any;

  // FINAL X p28-36. `gameFormat` picks the gameplay layout; options mirror
  // Game.GAME_FORMATS in common/models/game.js. Kept separate from
  // `gameType`, which is the restart lifecycle flag (new/restarted).
  gameFormats: { value: string; label: string }[] = [
    { value: "standard", label: "Standard" },
    { value: "seatMap", label: "Seat Map" },
    { value: "dressUp", label: "Dress Up" },
    { value: "autoTriggerMatch", label: "Auto-trigger AutoMatch" },
  ];

  // Dress Up: 6 groups of 8 emojis (hats, glasses, shirts, pants, shoes,
  // necklaces), validated server-side in Game.validate('dressUpGroups', ...).
  dressUpGroups: DressUpGroup[] = createEmptyDressUpGroups();
  dressUpFigure: any = null;
  dressUpFigureUrl: any;

  menuArt1stColor: any = "#efeeee";
  menuArt2ndColor: any = "#efeeee";

  inviteArt: any;
  inviteArtUrl: any;
  prizeData: any;
  editedGameId: string = "";
  categories: GameCategory[] = [];
  inviteImageTitle;
  inviteImageArtImage;
  disabled = false;
  gridType: any;
  noOfPrize = 0;
  queryParams: any;
  drawDateFlag = false;
  playerContent: any[] = [];
  @ViewChild("fileInput") fileInput: ElementRef;

  screenEmojisOptions: SortablejsOptions = {
    group: "normal-group",
  };
  cashPrizeImagesOptions: SortablejsOptions = {
    group: "normal-group",
  };

  tabForm: FormGroup;
  prizeForm: FormGroup;

  noOfFormControls = new Array<number>(48);

  public noOfTabs: Array<any> = [
    { screenNo: "Screen 1" },
    { screenNo: "Screen 2" },
    { screenNo: "Screen 3" },
  ];

  invitesData: any;

  // Vertical Stepper
  verticalStepperStep1: FormGroup;
  verticalStepperStep2: FormGroup;
  verticalStepperStep3: FormGroup;
  verticalStepperStep5: FormGroup;
  verticalStepperStep1Errors: any;
  verticalStepperStep2Errors: any;
  verticalStepperStep3Errors: any;
  verticalStepperStep5Errors: any;
  noOfControls: any;
  logoStretch = false;
  inviteArtStretch = false;
  imageScale = 0.5;

  layerImageScale = 0.5;
  showInGameplay = false;

  onEmojisChanged: Subscription;
  rule: any = "";
  dialogRef: any;

  confirmationDialogRef: MatDialogRef<ConfirmationDialogComponent>;
  matchEditorDialogRef: MatDialogRef<MatchEditorComponent>;
  exactMatchCheck: any[] = [true, true, true, true, true, true];
  emojis = [];

  min = new Date();
  emojisPerScreen = 16;
  popupData: any;

  inviteType = "image";
  selectedId = 0;
  imageRuleStretch = false;

  extraEmojisDatabase: ExtraEmojisDatabase | null;
  allGlobalEmojis: any[] = [];
  extra: Emoji[] = [];

  @ViewChild("paginator") paginator: MatPaginator;
  @ViewChild("matPaginator") matPaginator: MatPaginator;



  // Private
  private _unsubscribeAll: Subject<any>;

  selected = null;

  stepperSelectedIndex = 0;

  clubInvites: ClubInvite[] = Array.from({ length: 6 }, function (_, i) {
    return {
      label: 'Select Type ' + (i + 1),
      value: 'none',
      canAddReward: false,
      reward: undefined
    };
  });;

  constructor(
    private formBuilder: FormBuilder,
    private emojiApi: EmojiApi,
    private snackBar: MatSnackBar,
    private prizeService: PrizeService,
    private adminApi: AdminApi,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private fuseSplashScreen: FuseSplashScreenService,
    public gameSettingsService: GameSettingsService,
    public generalSettingsService: GeneralSettingsService,
    private dateValidationService: DateValidationService,
    private cdref: ChangeDetectorRef,
    private helperService: HelperService,
    private gameSettingsStorageService: GameSettingsStorageService
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    // getting id for editing game
    this.queryParams = this.activatedRoute.queryParams;
    const params: any = this.activatedRoute.snapshot.params;
    this.gameSettingsService.populated = false;
    this.editedGameId = params.id;
    if (this.editedGameId) {
      this.prizeForm = this.gameSettingsService.prizeForm;
    } else {
      localStorage.setItem("newGameFlag", JSON.stringify(false));
    }
    this.game = new Game();

    if (!this.editedGameId) this.gameSettingsService.selectedSimilarGames = [];

    // Vertical Stepper form error
    this.verticalStepperStep1Errors = {
      isPrivate: {},
      title: {},
      value: {},
      categoryId: {},
      drawDate: {},
      sharingContentCountdown: {},
      schedule: {},
      isRestartable: { value: false },
      isRegistration: { value: false },
      isLocation: {},
      isLocationProximity: {},
      restartThreshold: {},
      cycles: {},
      textRules: {},
      embededVideoRules: {},
      imageRule: {},
      rule: {},
      ruleTextRange: {},
    };

    this.verticalStepperStep5Errors = {
      logo: {},
      gameMenuArt: {},
      gameMenuLayer: {},
      inviteImageArtTitle: {},
      inviteImageArtImage: {},
      inviteImageUrl: {},
      menuArt1stColor: {},
      menuArt2ndColor: {},
      isRounded: {},
      surveyOnly: false,
    };

    this.verticalStepperStep5Errors = {};
  }

  getDefaultConfirmationText() {
    let atLeast18 = "";
    if (this.generalSettingsService.allResources && this.generalSettingsService.allResources['registrationForm']) {
      const entry = this.generalSettingsService.allResources['registrationForm'].find(r => r.content.data.key === 'atLeast18');
      if (entry) atLeast18 = entry.content.data.value;
    }
    return { original: atLeast18, translations: {} };
  }



  ngAfterViewInit(): void {
    this.extraEmojisDatabase = new ExtraEmojisDatabase(this.emojiApi);
  }

  ngOnInit() {
    this.s3BucketUrl = environment.s3BucketUrl;
    this.invitesData = this.gameSettingsService.initializeInvites();
    // initializing emoji screens
    if (localStorage["emojis"]) {
      let emojis = JSON.parse(localStorage["emojis"]);
      this.gameSettingsService.extraEmojisSet = emojis;
    }

    this.emojiApi
      .find({ where: { isActive: true }, order: "order ASC", limit: 1000 })
      .subscribe((emojis: any) => {
        this.allGlobalEmojis = Array.isArray(emojis) ? emojis : Object.values(emojis || {});
        this.syncDressUpToExtraEmojisSet();
      });

    // After Populate is clicked, rebuild dressUpGroups from the updated extraEmojisSet
    // so that assignEmojisToInviteImageArt(), game preview, Match Editor, and
    // Submitted Emojis Screen all reflect the newly populated emojis.
    this.gameSettingsService.emojisPopulated$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        if (this.verticalStepperStep1 && this.verticalStepperStep1.value.gameFormat === 'dressUp') {
          this.populateDefaultDressUpGroups();
        } else {
          this.game.emojis = [...this.gameSettingsService.extraEmojisSet];
        }
      });

    this.prizes = this.gameSettingsService.prizes;

    // All Categories in Database
    this.categories = this.gameSettingsService.categories;
    // Reactive Form
    this.form = this.formBuilder.group({
      title: ["", Validators.required],
      value: ["", Validators.required],
      categoryId: ["", Validators.required],
      logo: [undefined, Validators.required],
      gameMenuArt: [undefined, Validators.required],
      gameMenuLayer: [undefined],
      inviteImageArtTitle: [undefined],
      inviteImageArtImage: [undefined],
      inviteImageUrl: ["", Validators.required],
      drawDate: ["", Validators.required],
      isRestartable: [false],
      isRegistration: [false],
      isLocation: [false],
      isLocationProximity: [false],
      restartThreshold: [0],
      cycles: [0],
      textRules: [""],
      embededVideoRules: [""],
      imageRule: [""],
      rule: [""],
      ruleTextRange: [""],
      isPrivate: [
        { value: false, disabled: this.queryParams.value.action === "edit" },
      ],
      gridType: ["fixed"],
      isRounded: [false],
      submittedEmojiSmallFormat: [false],
      gamePlayEmojiBackground: [false],
      isFill: [false],
      isCompressed: [true],
      surveyOnly: [false],
    });

    this.tabForm = this.formBuilder.group({
      popUp: this.formBuilder.array([]),
    });

    if (!this.editedGameId) {
      for (let i = 0; i < this.noOfFormControls.length; i++) {
        const control = <FormArray>this.tabForm.controls["popUp"];
        control.push(this.gameSettingsService.creatingPopUpFormControls(i));
      }
    }

    if (
      this.editedGameId &&
      prizeButtons
        .map((el) => el.type)
        .includes(this.gameSettingsService.game.prizeMode)
    ) {
      const numOfPrizes = prizeButtons.find(
        (el) => el.type === this.gameSettingsService.game.prizeMode
      ).numOfPrizes;
      this.createPrizeForm(numOfPrizes);
      this.prizeForm.patchValue({
        prize: this.gameSettingsService.prizeControls,
      });
    }

    let step1;
    if (localStorage["step1"]) {
      step1 = JSON.parse(localStorage["step1"]);
    }
    // Vertical Stepper form stepper
    this.verticalStepperStep1 = this.formBuilder.group(
      {
        isPrivate: [
          {
            value: step1 && step1.isPrivate ? step1.isPrivate : false,
            disabled: this.queryParams.value.action === "edit",
          },
        ],
        title: [step1 && step1.title ? step1.title : "", Validators.required],
        value: [step1 && step1.value ? step1.value : ""],
        categoryId: [
          step1 && step1.categoryId ? step1.categoryId : "",
          Validators.required,
        ],
        gameFormat: [step1 && step1.gameFormat ? step1.gameFormat : "standard"],
        drawDate: [
          step1 && step1.drawDate ? step1.drawDate : "",
          Validators.required,
        ],
        sharingContentCountdown: [
          step1 && step1.sharingContentCountdown
            ? step1.sharingContentCountdown
            : null,
        ],
        schedule: [step1 && step1.schedule ? step1.schedule : null],
        isRestartable: [false],
        isRegistration: [false],
        isLocation: [step1 && step1.isLocation ? step1.isLocation : false],
        isLocationProximity: [
          step1 && step1.isLocationProximity
            ? step1.isLocationProximity
            : false,
        ],
        restartThreshold: [
          step1 && step1.restartThreshold ? step1.restartThreshold : 0,
        ],
        cycles: [step1 && step1.cycles ? step1.cycles : 0],
        textRules: [step1 && step1.textRules ? step1.textRules : ""],
        embededVideoRules: [
          step1 && step1.embededVideoRules ? step1.embededVideoRules : "",
        ],
        imageRule: [step1 && step1.imageRule ? step1.imageRule : ""],
        rule: [step1 && step1.rule ? step1.rule : ""],
        ruleTextRange: [
          step1 && step1.ruleTextRange ? step1.ruleTextRange : 230,
        ],
        confirmationText: this.formBuilder.group({
          original: [
            (step1 && step1.confirmationText && step1.confirmationText.original) ? step1.confirmationText.original : this.getDefaultConfirmationText().original
          ],
          translations: [
            (step1 && step1.confirmationText && step1.confirmationText.translations) ? step1.confirmationText.translations : {}
          ]
        }),
        embedWithTextContent: this.formBuilder.group({
          original: [
            (step1 && step1.embedWithTextContent && step1.embedWithTextContent.original) ? step1.embedWithTextContent.original : ""
          ],
          translations: [
            (step1 && step1.embedWithTextContent && step1.embedWithTextContent.translations) ? step1.embedWithTextContent.translations : {}
          ]
        }),
        imageWithTextContent: this.formBuilder.group({
          original: [
            (step1 && step1.imageWithTextContent && step1.imageWithTextContent.original) ? step1.imageWithTextContent.original : ""
          ],
          translations: [
            (step1 && step1.imageWithTextContent && step1.imageWithTextContent.translations) ? step1.imageWithTextContent.translations : {}
          ]
        }),
      },
      {
        validator: this.customValidator("ruleTextRange"),
      }
    );

    // ✅ Apply additional validators after group creation
    this.verticalStepperStep1.setValidators(
      this.dateValidationService.scheduleValidator()
    );

    let step2;
    if (localStorage["step2"]) {
      step2 = JSON.parse(localStorage["step2"]);
    }
    this.verticalStepperStep2 = this.formBuilder.group({
      gridType: [step2 && step2.gridType ? step2.gridType : "fixed"],
      isRounded: [step2 && step2.isRounded ? step2.isRounded : false],
      submittedEmojiSmallFormat: [step2 && step2.submittedEmojiSmallFormat ? step2.submittedEmojiSmallFormat : false],
      gamePlayEmojiBackground: [step2 && step2.gamePlayEmojiBackground ? step2.gamePlayEmojiBackground : false],
      gameplayEmojiPosition: [step2 && step2.gameplayEmojiPosition ? step2.gameplayEmojiPosition : "top"],
      isFill: [step2 && step2.isFill ? step2.isFill : false],
      isCompressed: [step2 && step2.isCompressed ? step2.isCompressed : true],
    });

    this.verticalStepperStep3 = this.formBuilder.group({});

    let step4;
    if (localStorage["step4"]) {
      step4 = JSON.parse(localStorage["step4"]);
    }

    let step5;
    if (localStorage["step5"]) {
      step5 = JSON.parse(localStorage["step5"]);
    }
    this.verticalStepperStep5 = this.formBuilder.group({
      logo: [undefined, Validators.required],
      gameMenuArt: [undefined, Validators.required],
      gameMenuLayer: [undefined],
      automatchButtonArt: [undefined],
      selfPickButtonArt: [undefined],
      inviteImageArtTitle: [undefined],
      inviteImageArtImage: [undefined],
      inviteImageUrl: [undefined],
      isSwipeMenu: [false],
      menuArt1stColor: [
        step5 && step5.menuArt1stColor ? step5.menuArt1stColor : "#efeeee",
      ],
      menuArt2ndColor: [
        step5 && step5.menuArt2ndColor ? step5.menuArt2ndColor : "#efeeee",
      ],
      surveyOnly: step5 && step5.surveyOnly ? step5.surveyOnly : false,
    });

    this.menuArt1stColor = this.verticalStepperStep5.value.menuArt1stColor;
    this.menuArt2ndColor = this.verticalStepperStep5.value.menuArt2ndColor;


    this.rule = step5 && step5.rule ? step5.rule : "";

    this.tabForm.controls["popUp"].valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((res) => {
        if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
          localStorage.setItem("popUp", JSON.stringify(res));
        }
      });

    this.verticalStepperStep1.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((value) => {
        if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
          localStorage.setItem("step1", JSON.stringify(value));
        }
      });

    this.verticalStepperStep2.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((res) => {
        if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
          localStorage.setItem("step2", JSON.stringify(res));
        }
      });

    this.verticalStepperStep5ValueChanges();

    if (!this.game.grid) {
      this.gridType = "4";
    }
    this.initializePrizeMode();
    if (!this.editedGameId && !this.game.prizeMode) {
      this.game.prizeMode = "auto select mode";
      if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
        localStorage.setItem("prizeMode", this.game.prizeMode);
      }
      this.createPrizeForm(1);
    }
    // initializing selectedPlayers if there isnt any player selected
    if (!this.game.selectedPlayers) {
      this.game.selectedPlayers = [];
    }

    // if data is saved in localStorage then data will be set accordingly
    this.getDataFromCookies();

    // calling game Editing Function
    this.gameEditing(this.editedGameId);
    if (this.gridType !== "6")
      this.gameSettingsService.gameEmojis =
        this.gameSettingsService.screensEmojis;
  }

  ngDoCheck(): void {
    // winPattern limit check
    if (this.gameSettingsService.winPattern.length > 0) {
      this.gameSettingsService.winPattern.forEach((pattern) => {
        if (pattern.cashPrizeImages && pattern.cashPrizeImages.length >= 6) {
          this.cashPrizeImagesOptions = {
            group: {
              name: "normal-group",
              put: false,
              pull: true,
            },
          };
        } else {
          this.cashPrizeImagesOptions = {
            group: {
              name: "normal-group",
              put: true,
              pull: true,
            },
          };
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.gameSettingsService.isDuplicationFlag = false;
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onScheduleChange(): void {
    // Trigger validation for the drawDate field only
    const drawDateControl = this.verticalStepperStep1.get("drawDate");
    if (drawDateControl) {
      drawDateControl.updateValueAndValidity();
    }
  }

  toggleState() {
    const text = this.verticalStepperStep5.value.text;
    if (text === "white") {
      this.verticalStepperStep5.patchValue({ text: "grey" });
    } else if (text === "grey") {
      this.verticalStepperStep5.patchValue({ text: "black" });
    } else {
      this.verticalStepperStep5.patchValue({ text: "white" });
    }
  }

  verticalStepperStep5ValueChanges() {
    this.verticalStepperStep5.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((value) => {
        if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
          localStorage.setItem("step4", JSON.stringify(value));
        }
        const gameMenuArtControl = this.verticalStepperStep5.get("gameMenuArt");
        const logoControl = this.verticalStepperStep5.get("logo");
        if (value.logo === "" && value.gameMenuArt !== "") {
          gameMenuArtControl.setValidators([Validators.required]);
          logoControl.clearValidators();
          alert("Please upload game menu art!");
        } else if (value.logo !== "" && value.gameMenuArt === "") {
          gameMenuArtControl.clearValidators();
          logoControl.setValidators([Validators.required]);
          alert("Please upload game logo!");
        } else if (value.logo === "" && value.gameMenuArt === "") {
          alert("Please upload game menu art and game logo!");
        }
        gameMenuArtControl.updateValueAndValidity({ emitEvent: false });
        logoControl.updateValueAndValidity({ emitEvent: false });
      });
  }

  initializePrizeMode() {
    if (localStorage["prizeMode"]) {
      this.game.prizeMode = localStorage["prizeMode"];
      if (this.game.prizeMode === "auto select mode") {
        this.createPrizeForm(1);
      } else if (
        prizeButtons.map((el) => el.type).includes(this.game.prizeMode)
      ) {
        const numOfPrizes = prizeButtons.find(
          (el) => el.type === this.game.prizeMode
        ).numOfPrizes;
        this.createPrizeForm(numOfPrizes);
      }
      if (localStorage["prize"]) {
        let prizes = JSON.parse(localStorage["prize"]);
        if (localStorage["prizeForm"]) {
          this.prizeForm.patchValue({
            prize: JSON.parse(localStorage["prizeForm"]),
          });
        }
        this.settingPrizesFormCookies(prizes);
      }
    }
  }

  getDataFromCookies() {
    this.gameSettingsStorageService.applyGameSettings(
      this, // parent component
      this.form, // main form
      this.verticalStepperStep5,
      prizeButtons // prizeButtons array
    );

    this.cdref.detectChanges();
  }

  settingPrizesFormCookies(prizes) {
    prizes.forEach((prize) => {
      if (prize.type === "exact match") {
        this.gameSettingsService.winPattern[prize.position] = {
          index: prize.position,
          type: prize.type,
          cashPrizeImages: prize.winPattern,
          cashPrizeValue: prize.cashPrizeValue,
          id: prize.id,
          image1: {
            image: prize.image1 ? prize.image1.image : "",
            isStretch: prize.image1 ? prize.image1.isStretch : false,
            description: prize.image1 ? prize.image1.description || "" : "",
          },
        };
      } else if (
        prizeButtons.map((el) => el.type).includes(this.game.prizeMode)
      ) {
        this.gameSettingsService.autoSelectPrizes[prize.position] = {
          title: prize.title,
          description: prize.description,
          buyNowUrl: prize.buyNowUrl || null,
          position: prize.position,
          type: prize.type,
          id: prize.id,
          media: prize.media,
        };
        if (
          prize.type === "auto select" ||
          prize.type === "solo" ||
          prize.type === "top 6"
        ) {
          this.gameSettingsService.autoSelectPrizes[prize.position].image1 = {
            image: prize.image1 ? prize.image1.image : "",
            isStretch: prize.image1 ? prize.image1.isStretch : false,
            description: prize.image1 ? prize.image1.description || "" : "",
          };
        }
        if (prize.type === "auto select") {
          this.gameSettingsService.autoSelectPrizes[prize.position].image2 = {
            image: prize.image2 ? prize.image2.image : "",
            isStretch: prize.image2 ? prize.image2.isStretch : false,
            description: prize.image2 ? prize.image2.description || "" : "",
          };
          this.gameSettingsService.autoSelectPrizes[prize.position].image3 = {
            image: prize.image3 ? prize.image3.image : "",
            isStretch: prize.image3 ? prize.image3.isStretch : false,
            description: prize.image3 ? prize.image3.description || "" : "",
          };
          if (prize.image4) {
            this.gameSettingsService.autoSelectPrizes[prize.position].image4 = {
              image: prize.image4.image,
              isStretch: prize.image4.isStretch,
              description: prize.image4.description || "",
            };
          }
        }
        if (prize.type === "solo") {
          this.gameSettingsService.autoSelectPrizes[prize.position].image2 = {
            image: prize.image2.image,
            isStretch: prize.image2.isStretch,
            description: prize.image2.description || "",
          };
          this.gameSettingsService.autoSelectPrizes[prize.position].image3 = {
            image: prize.image3.image,
            isStretch: prize.image3.isStretch,
            description: prize.image3.description || "",
          };
          this.gameSettingsService.autoSelectPrizes[prize.position].image4 = {
            image: prize.image4.image,
            isStretch: prize.image4.isStretch,
            description: prize.image4.description || "",
          };
          this.gameSettingsService.autoSelectPrizes[prize.position].image5 = {
            image: prize.image5.image,
            isStretch: prize.image5.isStretch,
            description: prize.image5.description || "",
          };
          this.gameSettingsService.autoSelectPrizes[prize.position].image6 = {
            image: prize.image6.image,
            isStretch: prize.image6.isStretch,
            description: prize.image6.description || "",
          };
        }
      } else if (this.game.prizeMode === "auto select mode") {
        if (prize.type === "top 6") {
          this.gameSettingsService.autoSelectPrizes[prize.position] = {
            title: prize.title,
            description: prize.description,
            buyNowUrl: prize.buyNowUrl || null,
            image1: {
              image: prize.image1.image,
              isStretch: prize.image1.isStretch,
              description: prize.image1.description || "",
            },
            position: prize.position,
            type: prize.type,
            id: prize.id,
          };
        } else if (prize.type === "auto select") {
          this.gameSettingsService.autoSelectPrizes[prize.position] = {
            title: prize.title,
            description: prize.description,
            buyNowUrl: prize.buyNowUrl || null,
            image1: {
              image: prize.image1.image,
              isStretch: prize.image1.isStretch,
              description: prize.image1.description || "",
            },
            image2: {
              image: prize.image2.image,
              isStretch: prize.image2.isStretch,
              description: prize.image2.description || "",
            },
            image3: {
              image: prize.image3.image,
              isStretch: prize.image3.isStretch,
              description: prize.image3.description || "",
            },
            image4: prize.image4
              ? {
                image: prize.image4.image,
                isStretch: prize.image4.isStretch,
                description: prize.image4.description || "",
              }
              : undefined,
            position: prize.position,
            type: prize.type,
            id: prize.id,
            media: prize.media,
          };
        } else if (prize.type === "solo") {
          this.gameSettingsService.autoSelectPrizes[prize.position] = {
            title: prize.title,
            description: prize.description,
            buyNowUrl: prize.buyNowUrl || null,
            image1: {
              image: prize.image1 ? prize.image1.image : "",
              isStretch: prize.image1 ? prize.image1.isStretch : false,
              description: prize.image1.description || "",
            },
            image2: {
              image: prize.image2 ? prize.image2.image : "",
              isStretch: prize.image2 ? prize.image2.isStretch : false,
              description: prize.image2.description || "",
            },
            image3: {
              image: prize.image3 ? prize.image3.image : "",
              isStretch: prize.image3 ? prize.image3.isStretch : false,
              description: prize.image3.description || "",
            },
            image4: {
              image: prize.image4 ? prize.image4.image : "",
              isStretch: prize.image4 ? prize.image4.isStretch : false,
              description: prize.image4.description || "",
            },
            image5: {
              image: prize.image5 ? prize.image5.image : "",
              isStretch: prize.image5 ? prize.image5.isStretch : false,
              description: prize.image5.description || "",
            },
            image6: {
              image: prize.image6 ? prize.image6.image : "",
              isStretch: prize.image6 ? prize.image6.isStretch : false,
              description: prize.image6.description || "",
            },
            position: prize.position,
            type: prize.type,
            id: prize.id,
          };
        }
      }
    });
  }

  customValidator = (ruleTextRange) => {
    return (group: FormGroup) => {
      const ruleTextRangeInput = group.controls[ruleTextRange];
      ruleTextRangeInput.valueChanges
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((value) => {
          if (value > 2000) {
            ruleTextRangeInput.setErrors({ notEquivalent: true });
            return ruleTextRangeInput.setErrors({ notEquivalent: true });
          } else {
            return ruleTextRangeInput.setErrors(null);
          }
        });
    };
  };

  receivePopup($event) {
    const popUpArray = this.tabForm.getRawValue();
    this.popupData = $event;
    const index = this.popupData.position - 1;
    popUpArray.popUp[index]["adType" + index] = this.popupData.type
      ? this.popupData.type
      : "ad";
    popUpArray.popUp[index]["contentTabType" + index] =
      this.popupData.content && this.popupData.content.type
        ? this.popupData.content.type
        : "CONTENT";
    if (
      this.popupData.type === "ad" &&
      this.popupData.content &&
      this.popupData.content.type === "CONTENT"
    ) {
      popUpArray.popUp[index]["sfxSound" + index] = "sfxSound";
      popUpArray.popUp[index]["countDown" + index] = 0;
    }
    // removing sfx and setting timer to 0 upon changing admob, manad to ad
    if (
      this.popupData.type === "ad" &&
      this.popupData.content &&
      (_.isEqual(this.popupData.content, {}) ||
        (_.isUndefined(this.popupData.content.data) && // if data is empty and type is also empty
          _.isEqual(this.popupData.content.type, "")))
    ) {
      popUpArray.popUp[index]["sfxSound" + index] = "sfxSound";
      popUpArray.popUp[index]["countDown" + index] = 0;
    }
    popUpArray.popUp[index]["popUp" + index] = this.popupData;
    this.tabForm.patchValue({ popUp: popUpArray.popUp });
  }

  gameSettingSelection(inviteType) {
    this.inviteType = inviteType;
    localStorage.setItem("inviteType", inviteType);
  }

  gameMenuArtSizeSelection(manuArtSizeNavigation) {
    this.manuArtSizeNavigation = manuArtSizeNavigation;
    this.gameSettingsService.isGameMenuToolbar = false;
    localStorage.setItem("manuArtSizeNavigation", manuArtSizeNavigation);
  }

  /**
   * changing grid emojis on grid selection
   * updating Game Emojis according to grid selection
   * grid Types
   * 4: 4 cols per screens
   * 2: 2 cols per screens
   * 6: 6 cols per screens
   * ? need to verify this function wheather all the functionality which is performing in that function is neccessary or not
   */
  isGridSelected(grid: any): boolean {
    const currentFormat = this.verticalStepperStep1 && this.verticalStepperStep1.value && this.verticalStepperStep1.value.gameFormat
      ? this.verticalStepperStep1.value.gameFormat
      : "standard";
    if (grid.gameFormat && grid.gameFormat !== "standard") {
      return currentFormat === grid.gameFormat;
    }
    return currentFormat === "standard" && (this.gridType === grid.noOfCol || (grid.key === "6" && this.gridType === "6"));
  }

  isDressUpGroupsEmpty(): boolean {
    if (!Array.isArray(this.dressUpGroups) || this.dressUpGroups.length === 0) return true;
    return this.dressUpGroups.every(g => !g.emojiIds || g.emojiIds.length === 0);
  }

  populateDefaultDressUpGroups() {
    const defaults = (this.gameSettingsService.extraEmojisSet && this.gameSettingsService.extraEmojisSet.length >= 48)
      ? this.gameSettingsService.extraEmojisSet
      : ((this.gameSettingsService.defaultEmojis && this.gameSettingsService.defaultEmojis.length >= 48)
        ? this.gameSettingsService.defaultEmojis
        : this.gameSettingsService.emojis);

    if (defaults && defaults.length >= 48) {
      const bodyPartNames = ["hats", "glasses", "shirts", "pants", "shoes", "necklaces"];
      this.dressUpGroups = bodyPartNames.map((bodyPart, idx) => {
        const slice = defaults.slice(idx * 8, idx * 8 + 8);
        return {
          bodyPart,
          emojiIds: slice.map((e: any) => {
            if (!e) return '';
            if (typeof e === 'string') return e;
            return (
              e.id ||
              e._id ||
              e.emojiId ||
              (e.emoji && (e.emoji.id || e.emoji._id)) ||
              ''
            );
          }).filter((id: string) => !!id && id !== '[object Object]')
        };
      });
      this.syncDressUpToExtraEmojisSet();
    }
  }

  syncDressUpToExtraEmojisSet() {
    if (this.verticalStepperStep1 && this.verticalStepperStep1.value.gameFormat === 'dressUp') {
      const pool = [
        ...(Array.isArray(this.allGlobalEmojis) ? this.allGlobalEmojis : []),
        ...(Array.isArray(this.gameSettingsService.defaultEmojis) ? this.gameSettingsService.defaultEmojis : []),
        ...(Array.isArray(this.gameSettingsService.extraEmojisSet) ? this.gameSettingsService.extraEmojisSet : []),
        ...(Array.isArray(this.gameSettingsService.emojis) ? this.gameSettingsService.emojis : []),
        ...(Array.isArray(this.extra) ? this.extra : [])
      ];
      const emojiMap: { [id: string]: any } = {};
      pool.forEach(e => {
        if (!e) return;
        const unwrapped = e.emoji || e;
        const rawImg = unwrapped.image || e.image || '';
        const target = {
          ...unwrapped,
          ...e,
          image: rawImg ? rawImg.replace(this.s3BucketUrl, '') : '',
          isActive: unwrapped.isActive !== false && e.isActive !== false
        };
        const id = e.id || e._id || e.emojiId || (e.emoji && (e.emoji.id || e.emoji._id));
        if (id) emojiMap[String(id)] = target;
        if (e.id) emojiMap[String(e.id)] = target;
        if (e._id) emojiMap[String(e._id)] = target;
        if (e.emojiId) emojiMap[String(e.emojiId)] = target;
        if (e.emoji) {
          if (e.emoji.id) emojiMap[String(e.emoji.id)] = target;
          if (e.emoji._id) emojiMap[String(e.emoji._id)] = target;
        }
      });

      let allResolved: any[] = [];
      const bodyPartNames = ["hats", "glasses", "shirts", "pants", "shoes", "necklaces"];
      bodyPartNames.forEach(bp => {
        const group = Array.isArray(this.dressUpGroups) ? this.dressUpGroups.find(g => g.bodyPart === bp) : null;
        if (group && Array.isArray(group.emojiIds)) {
          group.emojiIds.forEach(id => {
            const resolved = emojiMap[String(id)];
            if (resolved) allResolved.push(resolved);
          });
        }
      });

      if (allResolved.length > 0) {
        this.gameSettingsService.extraEmojisSet = [...allResolved];
        this.game.emojis = [...allResolved];
      }
    }
  }

  selectGridFormat(grid: any) {
    if (!grid) return;
    const format = grid.gameFormat || "standard";
    if (this.verticalStepperStep1) {
      this.verticalStepperStep1.patchValue({ gameFormat: format });
    }
    if (format === 'dressUp' && this.isDressUpGroupsEmpty()) {
      this.populateDefaultDressUpGroups();
    }
    this.gridSelection(grid.noOfCol || "6");
  }

  gridSelection(gridType) {
    if (!this.gameSettingsService.game.grid) {
      this.gameSettingsService.game.grid = {};
    }
    this.gameSettingsService.game.grid.noOfColoumns = this.gameSettingsService
      .game.grid.noOfColoumns
      ? this.gameSettingsService.game.grid.noOfColoumns
      : gridType;
    this.emojisPerScreen = gridType === "4" ? 16 : 6;
    if (
      gridType === "4" &&
      this.gameSettingsService.defaultEmojis.length !== 0 &&
      this.gameSettingsService.gameEmojis.length === 0
    ) {
      this.game.emojis =
        this.game.emojis && this.game.emojis.length !== 0
          ? this.game.emojis
          : this.gameSettingsService.defaultEmojis.slice(0, 48);

      this.gameSettingsService.screensEmojis = [...this.game.emojis];
      this.gridType = gridType;
      this.noOfTabs = [
        { screenNo: "Screen 1" },
        { screenNo: "Screen 2" },
        { screenNo: "Screen 3" },
      ];
    }
    if (
      gridType === "4" &&
      this.gameSettingsService.defaultEmojis.length === 0 &&
      this.gameSettingsService.gameEmojis.length === 0
    ) {
      this.game.emojis =
        this.game.emojis && this.game.emojis.length !== 0
          ? this.game.emojis
          : this.gameSettingsService.emojis.slice(0, 48);

      this.gameSettingsService.screensEmojis = [...this.game.emojis];
      this.gridType = gridType;
      this.noOfTabs = [
        { screenNo: "Screen 1" },
        { screenNo: "Screen 2" },
        { screenNo: "Screen 3" },
      ];
    }
    if (gridType === "2" && this.gameSettingsService.gameEmojis.length === 0) {
      this.gridType = gridType;
      this.noOfTabs = [
        { screenNo: "Screen 1" },
        { screenNo: "Screen 2" },
        { screenNo: "Screen 3" },
        { screenNo: "Screen 4" },
        { screenNo: "Screen 5" },
        { screenNo: "Screen 6" },
        { screenNo: "Screen 7" },
        { screenNo: "Screen 8" },
      ];
    }
    if (gridType === "4" && this.gameSettingsService.gameEmojis.length !== 0) {
      const tempEmojis =
        this.gameSettingsService.game.grid.noOfColoumns === "4"
          ? this.game.emojis
          : this.gameSettingsService.gameEmojis;
      if (tempEmojis.length !== 0) {
        this.game.emojis = tempEmojis;
        if (
          this.gameSettingsService.game &&
          (this.gameSettingsService.game.grid.noOfColoumns === "2" ||
            this.gameSettingsService.game.grid.noOfColoumns === "6")
        ) {
          if (tempEmojis.filter((res) => res.emoji).length !== 0) {
            this.game.emojis =
              tempEmojis.filter((item) => item.emoji).length !== 0
                ? _.map(tempEmojis, "emoji")
                : tempEmojis;

            this.game.emojis.forEach((emoji) => {
              if (
                emoji.image &&
                emoji.image.indexOf(environment.s3BucketUrl) === -1
              ) {
                emoji.image = environment.s3BucketUrl + emoji.image;
              }
            });
          } else {
            this.game.emojis = tempEmojis;
          }
        }
      } else if (
        this.gameSettingsService.gameEmojis &&
        this.gameSettingsService.gameEmojis.length !== 0
      ) {
        this.game.emojis =
          this.gameSettingsService.gameEmojis.filter((item) => item.emoji)
            .length !== 0
            ? _.map(this.gameSettingsService.gameEmojis, "emoji")
            : this.gameSettingsService.gameEmojis;
      }
      if (_.map(this.game.emojis, "emoji").length !== 0) {
        this.game.emojis =
          this.game.emojis.filter((item) => item.emoji).length !== 0
            ? _.map(this.game.emojis, "emoji")
            : this.game.emojis;
      }
      this.game.emojis = [
        ...this.game.emojis.map((emoji) => ({
          ...emoji,
          image: emoji.image.replace(this.s3BucketUrl, ""),
        })),
      ];

      this.gameSettingsService.screensEmojis = [...this.game.emojis];
      this.gridType = gridType;
      this.noOfTabs = [
        { screenNo: "Screen 1" },
        { screenNo: "Screen 2" },
        { screenNo: "Screen 3" },
      ];
    }
    if (gridType === "2" && this.gameSettingsService.gameEmojis.length !== 0) {
      this.game.emojis = this.gameSettingsService.gameEmojis;
      this.gameSettingsService.screensEmojis = this.game.emojis;
      this.gridType = gridType;
      this.noOfTabs = [
        { screenNo: "Screen 1" },
        { screenNo: "Screen 2" },
        { screenNo: "Screen 3" },
        { screenNo: "Screen 4" },
        { screenNo: "Screen 5" },
        { screenNo: "Screen 6" },
        { screenNo: "Screen 7" },
        { screenNo: "Screen 8" },
      ];
    }
    if (gridType === "6" && this.gameSettingsService.gameEmojis.length !== 0) {
      this.game.emojis = this.gameSettingsService.gameEmojis;
      this.gameSettingsService.screensEmojis = this.game.emojis;
      this.emojisPerScreen = 48;
      this.gridType = gridType;
      this.noOfTabs = [{ screenNo: "Screen" }];
    }
    if (
      gridType === "6" &&
      this.gameSettingsService.defaultEmojis.length !== 0 &&
      this.gameSettingsService.gameEmojis.length === 0
    ) {
      this.game.emojis =
        this.game.emojis && this.game.emojis.length !== 0
          ? this.game.emojis
          : this.gameSettingsService.defaultEmojis.slice(0, 48);
      this.gameSettingsService.screensEmojis = this.game.emojis;
      this.emojisPerScreen = 48;
      this.gridType = gridType;
      this.noOfTabs = [{ screenNo: "Screen" }];
    }
    if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
      localStorage.setItem("gridType", this.gridType);
    }
  }

  selectPrizeMode(mode) {
    const count = prizeButtons.find((el) => el.type === mode).numOfPrizes;

    this.game.prizeMode = mode;
    this.noOfControls = count;

    this.createPrizeForm(this.noOfControls);
    this.gameSettingsService.winPattern = [];
    this.gameSettingsService.autoSelectPrizes = [];
    this.deleteWinPatterns = [];
    this.gameSettingsService.clearPrizeColorConfettiState(count);

    if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
      localStorage.setItem("prizeMode", mode);
    }
  }

  createPrizeForm(noOfControls) {
    this.prizeForm = this.formBuilder.group({
      prize: this.formBuilder.array([]),
    });
    for (let i = 0; i < noOfControls; i++) {
      const control = <FormArray>this.prizeForm.controls["prize"];
      control.push(this.gameSettingsService.creatingPrizeControls(i));
    }
  }

  checkExactOrEliminationInPrizes() {
    const prizeFormArray = this.prizeForm.getRawValue();
    return (
      prizeFormArray.prize.filter(
        (item, index) =>
          item["type" + index] === "exact match" ||
          item["type" + index] === "elimination"
      ).length !== 0
    );
  }

  selectedPrizeType(prizeType, index) {
    const prizeFormArray = this.prizeForm.getRawValue();
    if (this.prizeForm.value.prize[index]["type" + index] === "auto select") {
      prizeFormArray.prize[index]["cashPrizeValue" + index] = "";
      if (this.gameSettingsService.winPattern[index]) {
        if (this.gameSettingsService.winPattern[index].id) {
          this.gameSettingsService.autoSelectPrizes[index] = {
            id: this.gameSettingsService.winPattern[index].id,
            position: index,
          };
        }
        this.gameSettingsService.winPattern[index] = {};
        this.gameSettingsService.winPattern[index].cashPrizeImages = [];
      }
      this.prizeForm.patchValue({ prize: prizeFormArray.prize });
    }
    if (this.prizeForm.value.prize[index]["type" + index] === "") {
      prizeFormArray.prize[index]["cashPrizeValue" + index] = "";
      prizeFormArray.prize[index]["type" + index] = "deletePrize";
      if (this.gameSettingsService.winPattern[index] && !this.editedGameId) {
        this.gameSettingsService.winPattern[index] = {};
      }
      if (this.gameSettingsService.winPattern[index] && this.editedGameId) {
        this.gameSettingsService.winPattern[index].cashPrizeValue = "";
        this.gameSettingsService.winPattern[index].cashPrizeImages = [];
      }
      this.prizeForm.patchValue({ prize: prizeFormArray.prize });
    }
    if (this.prizeForm.value.prize[index]["type" + index] === "exact match") {
      if (this.gameSettingsService.autoSelectPrizes[index]) {
        if (this.gameSettingsService.autoSelectPrizes[index].id) {
          this.gameSettingsService.winPattern[index] = {
            id: this.gameSettingsService.autoSelectPrizes[index].id,
            position: index,
          };
        }
        this.gameSettingsService.autoSelectPrizes[index] = {};
      }
      if (this.gameSettingsService.winPattern[index]) {
        this.gameSettingsService.winPattern[index].type = prizeType;
        this.gameSettingsService.autoSelectPrizes[index] = {};
      } else {
        this.gameSettingsService.autoSelectPrizes[index] = {};
      }
      this.prizeForm.patchValue({ prize: prizeFormArray.prize });
    }
    this.saveInLocalStorage("prizeForm", prizeFormArray.prize);
  }

  selectPlayers() {
    this.game.selectedPlayers = !this.game.selectedPlayers
      ? []
      : this.game.selectedPlayers;
    const data = {
      action: this.editedGameId !== "" ? "edit" : "new",
      selectedPlayers:
        this.game.selectedPlayers.length !== 0 ? this.game.selectedPlayers : [],
      gameId: this.editedGameId !== "" ? "" : this.editedGameId,
      restart: this.queryParams._value.action,
    };
    this.dialogRef = this.dialog.open(PlayerListingComponent, {
      panelClass: "players-table-dialog",
      width: "600px",
      height: "570px",
      data: data,
    });

    this.dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((response: any) => {
        if (!response) {
          return;
        }
        this.game.selectedPlayers = this.gameSettingsService.selectedPlayers;
        // if (response._selected && response._selected.length !== 0) {
        //   this.game.selectedPlayers = [...this.game.selectedPlayers, ...response._selected];
        // }
        if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
          localStorage.setItem(
            "selectedPlayers",
            JSON.stringify(this.game.selectedPlayers)
          );
        }
      });
  }

  openPrizeDialog(index) {
    this.dialogRef = this.dialog.open(PrizeFormComponent, {
      panelClass: "prize-form-dialog",
      width: "550px",
      data: {
        action: this.gameSettingsService.autoSelectPrizes[index]
          ? "edit"
          : "new",
        prizeType: this.prizeForm.value.prize[index]["type" + index],
        prizeMode: this.game.prizeMode,
        prize: this.gameSettingsService.autoSelectPrizes[index]
          ? this.gameSettingsService.autoSelectPrizes[index]
          : {},
        index: index,
      },
    });

    this.dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((response: any) => {
        if (!response) {
          return;
        }
        const prizeData: any = {
          title: response.title,
          description: response.description,
          buyNowUrl: response.buyNowUrl || null,
          // media: response.media, // Removed as media is consolidated into images
          position: index,
          type: this.prizeForm.value.prize[index]["type" + index],
        };
        response.images.forEach((item, index) => {
          prizeData["image" + (index + 1)] = {
            image: item.image,
            isStretch: item.isStretch,
            description: item.description,
            type: item.type,
          };
        });
        if (
          this.gameSettingsService.autoSelectPrizes[index] &&
          this.gameSettingsService.autoSelectPrizes[index].id
        ) {
          prizeData.id = this.gameSettingsService.autoSelectPrizes[index].id;
        }

        if (
          this.gameSettingsService.winPattern[index] &&
          this.gameSettingsService.winPattern[index].id
        ) {
          prizeData.id = this.gameSettingsService.winPattern[index].id;
        }

        // Additional properties for specific confetti types
        const confettiConfig = this.gameSettingsService.prizeConfetti[index];

        if (confettiConfig.type === "color") {
          prizeData.prizeConfetti = confettiConfig.color;
        } else if (confettiConfig.type === "emoji") {
          prizeData.prizeConfetti = confettiConfig.emoji;
        } else {
          prizeData.prizeConfetti = confettiConfig.canvas;
        }

        this.gameSettingsService.autoSelectPrizes[index] = prizeData;

        // * setting isDuplication to true
        this.gameSettingsService.isDuplicationFlag = true;

        // console.log('on prize form dialog closed this.gameSettingsService.autoSelectPrizes...', this.gameSettingsService.autoSelectPrizes[index]);

        this.saveInLocalStorage("prize", this.gatheringPrizeData("data"));
      });
  }

  isPopUpEmbed(screenNo, index) {
    if (this.game.emojis !== undefined) {
      let i;
      if (this.gridType === "4") {
        i = screenNo * 16 + index;
      } else {
        i = screenNo * 6 + index;
      }
      return (
        this.game.emojis[i] &&
        this.game.emojis[i].popup &&
        this.game.emojis[i].popup.type &&
        this.game.emojis[i].popup.type === "embed"
      );
    }
    return false;
  }

  assignEmojisToInviteImageArt(noOfEmojis = false) {
    if (this.verticalStepperStep1 && this.verticalStepperStep1.value.gameFormat === 'dressUp') {
      const pool = [
        ...(Array.isArray(this.allGlobalEmojis) ? this.allGlobalEmojis : []),
        ...(Array.isArray(this.gameSettingsService.extraEmojisSet) ? this.gameSettingsService.extraEmojisSet : []),
        ...(Array.isArray(this.gameSettingsService.defaultEmojis) ? this.gameSettingsService.defaultEmojis : []),
        ...(Array.isArray(this.gameSettingsService.emojis) ? this.gameSettingsService.emojis : []),
        ...(Array.isArray(this.extra) ? this.extra : [])
      ];
      const emojiMap: { [id: string]: any } = {};
      pool.forEach(e => {
        if (!e) return;
        const unwrapped = e.emoji || e;
        const rawImg = unwrapped.image || e.image || '';
        const target = {
          ...unwrapped,
          ...e,
          image: rawImg ? rawImg.replace(this.s3BucketUrl, '') : '',
          isActive: unwrapped.isActive !== false && e.isActive !== false
        };
        const id = e.id || e._id || e.emojiId || (e.emoji && (e.emoji.id || e.emoji._id));
        if (id) emojiMap[String(id)] = target;
        if (e.id) emojiMap[String(e.id)] = target;
        if (e._id) emojiMap[String(e._id)] = target;
        if (e.emojiId) emojiMap[String(e.emojiId)] = target;
        if (e.emoji) {
          if (e.emoji.id) emojiMap[String(e.emoji.id)] = target;
          if (e.emoji._id) emojiMap[String(e.emoji._id)] = target;
        }
      });

      let dressUpResolved: any[] = [];
      const bodyPartNames = ["hats", "glasses", "shirts", "pants", "shoes", "necklaces"];
      bodyPartNames.forEach(bp => {
        const group = Array.isArray(this.dressUpGroups) ? this.dressUpGroups.find(g => g.bodyPart === bp) : null;
        if (group && Array.isArray(group.emojiIds)) {
          group.emojiIds.forEach(id => {
            const resolved = emojiMap[String(id)];
            if (resolved) dressUpResolved.push(resolved);
          });
        }
      });

      if (dressUpResolved.length > 0) {
        return noOfEmojis ? dressUpResolved : dressUpResolved.slice(0, 48);
      }
    }

    let emojisPerScreen =
      this.gridType === "4"
        ? 16
        : this.gridType === "2"
          ? 6
          : 48;
    let emojis = [
      ...this.gameSettingsService.reorganizeEmojisAndConcatenate(
        this.editedGameId,
        this.gameSettingsService.extraEmojisSet
      ),
    ];
    return noOfEmojis ? emojis : emojis.slice(0, emojisPerScreen);
  }

  deleteWinPatterns = [];
  extraImagesDelete(index, emoji) {
    this.confirmationDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: "Are you sure you want to Delete?",
      },
    });
    this.confirmationDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        // category deleting on selecting yes from confirmation dialog
        if (result) {
          this.gameSettingsService.winPattern[index].cashPrizeImages =
            this.gameSettingsService.winPattern[index].cashPrizeImages.filter(
              (cashPrizeEmoji) => !_.isEqual(cashPrizeEmoji, emoji)
            );
          if (this.deleteWinPatterns.length === 0) {
            this.deleteWinPatterns.push({
              winPatternIndex: index,
              deleteIndex: [emoji.index],
            });
          } else {
            let fIndex = this.deleteWinPatterns.findIndex(
              (el) => el.winPatternIndex === index
            );
            if (fIndex === -1) {
              this.deleteWinPatterns.push({
                winPatternIndex: index,
                deleteIndex: [emoji.index],
              });
            } else {
              this.deleteWinPatterns[fIndex].deleteIndex = [
                ...this.deleteWinPatterns[fIndex].deleteIndex,
                emoji.index,
              ];
            }
          }
        }
      });
  }

  settingPrizeConfetti(prize) {
    if (prize.prizeConfetti.type === "emoji") {
      this.gameSettingsService.prizeConfetti[prize.position].type = "emoji";
      this.gameSettingsService.autoSelectPrizes[prize.position].prizeConfetti =
        prize.prizeConfetti;
      this.gameSettingsService.prizeConfetti[prize.position].emoji =
        prize.prizeConfetti;
    } else if (prize.prizeConfetti.type === "color") {
      this.gameSettingsService.prizeConfetti[prize.position].type = "color";
      this.gameSettingsService.autoSelectPrizes[prize.position].prizeConfetti =
        prize.prizeConfetti;
      this.gameSettingsService.prizeConfetti[prize.position].color =
        prize.prizeConfetti;
    } else {
      this.gameSettingsService.prizeConfetti[prize.position].type = "canvas";
      this.gameSettingsService.autoSelectPrizes[prize.position].prizeConfetti =
        prize.prizeConfetti;
      this.gameSettingsService.prizeConfetti[prize.position].canvas =
        prize.prizeConfetti;
    }
  }

  /**
   * @param gameId
   */
  gameEditing = async (gameId) => {
    if (gameId) {
      this.prizeForm = this.gameSettingsService.prizeForm;
      let game = this.gameSettingsService.gameEditMode;
      this.game = game;
      this.gameSettingsService.isGameMenuToolbar = game.isGameMenuToolbar;
      this.game.prizes = game.prizes;
      this.gameLogo = game.logo.image;
      this.invitesData.invites =
        game.invites.length !== 0
          ? game.invites
          : this.gameSettingsService.initializeInvites().invites;
      this.inviteType = game.inviteType;
      this.invitesData.isInviteImageArtGradient = game.isInviteImageArtGradient
        ? game.isInviteImageArtGradient
        : false;
      this.invitesData.isProgressBarTransparent = game.isProgressBarTransparent
        ? game.isProgressBarTransparent
        : false;
      this.manuArtSizeNavigation = game.gameMenuArt.menuArtOption
        ? game.gameMenuArt.menuArtOption
        : this.manuArtSizeNavigation;
      this.menuArt = game.gameMenuArt.image;
      this.logoStretch = this.game.logo.isStretch;
      this.imageScale = this.game.gameMenuArt.imageScale;

      if (game.titleOpt) this.titleOpt = { ...this.titleOpt, ...game.titleOpt, showBackground: game.titleOpt.showBackground !== undefined ? !!game.titleOpt.showBackground : (game.titleOpt.position === 'top'), color: game.titleOpt.color || this.titleOpt.color || '#c4bfbf' };
      if (game.categoryOpt) this.categoryOpt = { ...this.categoryOpt, ...game.categoryOpt, showBackground: game.categoryOpt.showBackground !== undefined ? !!game.categoryOpt.showBackground : (game.categoryOpt.position === 'top'), color: game.categoryOpt.color || this.categoryOpt.color || '#c4bfbf' };
      if (game.shadowOpt) this.shadowOpt = { ...this.shadowOpt, ...game.shadowOpt, color: game.shadowOpt.color || this.shadowOpt.color || '#c4bfbf' };
      // FINAL X 2.3. A game saved before this field existed has no
      // `enterFreeOpt` at all — keep the component default (isActive: true)
      // rather than overwriting it with undefined.
      if (game.enterFreeOpt) this.enterFreeOpt = game.enterFreeOpt;
      // Same guard as enterFreeOpt: keep defaults for pre-field games.
      if (game.seatMapOpt) this.seatMapOpt = game.seatMapOpt;

      // setting layer image
      if (game && game.gameMenuLayer) {
        this.layer = game.gameMenuLayer.image;
        this.layerImageScale = game.gameMenuLayer.imageScale;

        if (game.gameMenuLayer.showInGameplay) {
          this.showInGameplay = game.gameMenuLayer.showInGameplay;
        }
      }

      if (game && game.automatchButtonArt) {
        this.automatchButtonArt = game.automatchButtonArt.image;
      }

      if (game && game.selfPickButtonArt) {
        this.selfPickButtonArt = game.selfPickButtonArt.image;
      }

      // FINAL X p28-36. Fall back to "standard" for pre-existing games that
      // predate gameFormat (undeclared/undefined on the loaded document).
      const gameFormat = game && game.gameFormat ? game.gameFormat : "standard";
      this.verticalStepperStep1.patchValue({ gameFormat });
      this.dressUpGroups = normalizeDressUpGroups(game && game.dressUpGroups);
      if (game && game.dressUpFigure) {
        this.dressUpFigure = game.dressUpFigure.image;
      }
      this.syncDressUpToExtraEmojisSet();

      if (this.game && this.game.rewards && this.game.rewards.length) {
        var rewards = this.game.rewards;

        this.clubInvites = this.clubInvites.map(function (invite, index) {
          var reward = rewards[index] || null;

          return Object.assign({}, invite, {
            reward: reward,
            canAddReward: true,
            value: reward && reward.type ? reward.type : 'none'
          });
        });
      }

      if (this.game.tieBreakerColorWheel) {
        this.gameSettingsService.tieBreakerColorWheel =
          this.game.tieBreakerColorWheel;
      }

      if (game && game.similarGames) {
        this.gameSettingsService.selectedSimilarGames = [...game.similarGames];
      }

      if (this.game.colorWheel) {
        const type = this.game.colorWheel.type;
        this.gameSettingsService.colorWheel.type = type;
        if (type === "img") {
          this.gameSettingsService.colorWheel.data.img = {
            file: null,
            src: this.game.colorWheel.img,
          };
        } else if (type === "java" || type === "css") {
          this.game.colorWheel.colors.forEach((el, index) => {
            this.gameSettingsService.colorWheel.data[`color_${index + 1}`] = el;
          });
        }

        if (
          this.game &&
          this.game.colorWheel &&
          this.game.colorWheel.cssCenterChoice
        ) {
          this.gameSettingsService.colorWheel.data.cssCenterChoice =
            this.game.colorWheel.cssCenterChoice;
        }
      }

      if (this.menuArt !== null) this.menuArtUrl = undefined;
      if (this.layer !== null) this.layerUrl = undefined;
      if (this.gameLogo !== null) this.gameLogoUrl = undefined;

      if (game.isLocation) {
        this.gameSettingsService.latitude = game.notifyLocation.lat;
        this.gameSettingsService.longitude = game.notifyLocation.lng;
        this.gameSettingsService.radius = game.locationRadius;
        this.gameSettingsService.unit = game.locationRadiusUnit;
      }

      this.verticalStepperStep5.get("gameMenuArt").setValidators(null);
      this.verticalStepperStep5.get("logo").setValidators(null);
      this.verticalStepperStep5.get("logo").updateValueAndValidity();
      this.verticalStepperStep5.get("gameMenuArt").updateValueAndValidity();

      if (game.powerBar) {
        this.invitesData.powerBar = {
          image: game.powerBar.image
            ? game.powerBar.image.replace(this.s3BucketUrl, "")
            : "",
          audio: game.powerBar.audio
            ? game.powerBar.audio.replace(this.s3BucketUrl, "")
            : "",
          isExpand: game.powerBar.isExpand,
        };
      }

      if (this.inviteType === "color-gradient" || this.inviteType === "layer") {
        this.menuArt1stColor = game.gameMenuArt.firstColor;
        this.menuArt2ndColor = game.gameMenuArt.secondColor;
        this.verticalStepperStep5.patchValue({
          menuArt1stColor: game.gameMenuArt.firstColor,
          menuArt2ndColor: game.gameMenuArt.secondColor,
        });
      }

      if (this.game.gameWinLosePreview && this.game.gameWinLosePreview.header) {
        this.dummyAssetsData.header = {
          ...this.dummyAssetsData.header,
          ...this.game.gameWinLosePreview.header
        };
      }
      if (this.game.gameWinLosePreview && this.game.gameWinLosePreview.prizeBackground) {
        this.dummyAssetsData.prizeBackground = {
          ...this.dummyAssetsData.prizeBackground,
          ...this.game.gameWinLosePreview.prizeBackground
        };
      }
      if (this.game.gameWinLosePreview && this.game.gameWinLosePreview.buttonBackground) {
        this.dummyAssetsData.buttonBackground = {
          ...this.dummyAssetsData.buttonBackground,
          ...this.game.gameWinLosePreview.buttonBackground
        };
      }
      if (this.game.gameTicket) {
        this.gameTicket = {
          ...this.gameTicket,
          ...this.game.gameTicket
        };
      }

      if (game.imageRule && game.imageRule.isStretch) {
        this.imageRuleStretch = this.game.imageRule.isStretch;
      }

      if (this.game.prizes) {
        this.game.prizes.forEach((prize) => {
          if (prize.type === "exact match") {
            this.gameSettingsService.winPattern[prize.position] = {
              index: prize.position,
              type: prize.type,
              cashPrizeImages: prize.winPattern,
              cashPrizeValue: prize.cashPrizeValue,
              image1: {
                image:
                  prize && prize.image1 && prize.image1.image
                    ? prize.image1.image
                    : null,
                isStretch: false,
                description: prize.image1 ? prize.image1.description || "" : "",
                type: prize.image1 ? prize.image1.type || "photo" : "photo",
              },
              prizeConfetti: prize.prizeConfetti,
              id: prize.id,
            };
          } else if (this.game.prizeMode !== "auto select mode") {
            this.gameSettingsService.autoSelectPrizes[prize.position] = {
              title: prize.title,
              description: prize.description,
              buyNowUrl: prize.buyNowUrl || null,
              position: prize.position,
              type: prize.type,
              id: prize.id,
            };
            if (prize.type === "auto select" || prize.type === "top 6") {

              // Assuming this.gameSettingsService.autoSelectPrizes is an array
              const updatedPrizes = [
                ...this.gameSettingsService.autoSelectPrizes,
              ];
              let image1 = null;
              let image2 = null;
              let image3 = null;
              if (prize.image1) {
                image1 = {
                  image: prize.image1 ? prize.image1.image : "",
                  isStretch: prize.image1 ? prize.image1.isStretch : false,
                  description: prize.image1 ? prize.image1.description || "" : "",
                  type: prize.image1.type || "photo",
                }
              }
              if (prize.image2) {
                image2 = {
                  image: prize.image2 ? prize.image2.image : "",
                  isStretch: prize.image2 ? prize.image2.isStretch : false,
                  description: prize.image2 ? prize.image2.description || "" : "",
                  type: prize.image2.type || "photo",
                }
              }
              if (prize.image3) {
                image3 = {
                  image: prize.image3 ? prize.image3.image : "",
                  isStretch: prize.image3 ? prize.image3.isStretch : false,
                  description: prize.image3 ? prize.image3.description || "" : "",
                  type: prize.image3.type || "photo",
                }
              }

              updatedPrizes[prize.position] = {
                ...updatedPrizes[prize.position],
                image1: image1,
                image2: image2,
                image3: image3,
              };

              this.gameSettingsService.autoSelectPrizes = updatedPrizes;
            }
            if (prize.type === "solo") {
              this.gameSettingsService.autoSelectPrizes[prize.position].image2 =
              {
                image: prize.image2 ? prize.image2.image : "",
                isStretch: prize.image2 ? prize.image2.isStretch : false,
                description: prize.image2 ? prize.image2.description || "" : "",
                type: prize.image2.type || "photo",
              };
              this.gameSettingsService.autoSelectPrizes[prize.position].image3 =
              {
                image: prize.image3 ? prize.image3.image : "",
                isStretch: prize.image3 ? prize.image3.isStretch : false,
                description: prize.image3 ? prize.image3.description || "" : "",
                type: prize.image3.type || "photo",
              };
              this.gameSettingsService.autoSelectPrizes[prize.position].image4 =
              {
                image: prize.image4 ? prize.image4.image : "",
                isStretch: prize.image4 ? prize.image4.isStretch : false,
                description: prize.image4 ? prize.image4.description || "" : "",
                type: prize.image4.type || "photo",
              };
              this.gameSettingsService.autoSelectPrizes[prize.position].image5 =
              {
                image: prize.image5 ? prize.image5.image : "",
                isStretch: prize.image5 ? prize.image5.isStretch : false,
                description: prize.image5 ? prize.image5.description || "" : "",
                type: prize.image5.type || "photo",
              };
              this.gameSettingsService.autoSelectPrizes[prize.position].image6 =
              {
                image: prize.image6 ? prize.image6.image : "",
                isStretch: prize.image6 ? prize.image6.isStretch : false,
                description: prize.image6 ? prize.image6.description || "" : "",
                type: prize.image6.type || "photo",
              };
            }

            // Add media if present
            if (prize.media) {
              this.gameSettingsService.autoSelectPrizes[prize.position].media = prize.media;
            }

            this.settingPrizeConfetti(prize);
          } else if (this.game.prizeMode === "auto select mode") {
            if (prize.type === "top 6") {
              this.gameSettingsService.autoSelectPrizes[prize.position] = {
                title: prize.title,
                description: prize.description,
                buyNowUrl: prize.buyNowUrl || null,
                image1: {
                  image: prize.image1 ? prize.image1.image : "",
                  isStretch: prize.image1 ? prize.image1.isStretch : false,
                  description: prize.image1 ? prize.image1.description || "" : "",
                  type: prize.image1.type || 'photo'
                },
                position: prize.position,
                type: prize.type,
                id: prize.id,
              };
              // Add media if present
              if (prize.media) {
                this.gameSettingsService.autoSelectPrizes[prize.position].media = prize.media;
              }
            } else if (prize.type === "auto select") {
              this.gameSettingsService.autoSelectPrizes[prize.position] = {
                title: prize.title,
                description: prize.description,
                buyNowUrl: prize.buyNowUrl || null,
                image1: {
                  image: prize.image1 ? prize.image1.image : "",
                  isStretch: prize.image1 ? prize.image1.isStretch : false,
                  description: prize.image1 ? prize.image1.description || "" : "",
                  type: prize.image1.type || "photo",
                },
                image2: {
                  image: prize.image2 ? prize.image2.image : "",
                  isStretch: prize.image2 ? prize.image2.isStretch : false,
                  description: prize.image2 ? prize.image2.description || "" : "",
                  type: prize.image2.type || 'photo',
                },
                image3: {
                  image: prize.image3 ? prize.image3.image : "",
                  isStretch: prize.image3 ? prize.image3.isStretch : false,
                  description: prize.image3 ? prize.image3.description || "" : "",
                  type: prize.image3.type || 'photo'
                },
                position: prize.position,
                type: prize.type,
                id: prize.id,
              };
              // Add media if present
              if (prize.media) {
                this.gameSettingsService.autoSelectPrizes[prize.position].media = prize.media;
              }
            } else if (prize.type === "solo") {
              this.gameSettingsService.autoSelectPrizes[prize.position] = {
                title: prize.title,
                description: prize.description,
                buyNowUrl: prize.buyNowUrl || null,
                image1: {
                  image: prize.image1 ? prize.image1.image : "",
                  isStretch: prize.image1 ? prize.image1.isStretch : false,
                  description: prize.image1.description || "",
                  type: prize.image1.type || 'photo'
                },
                image2: {
                  image: prize.image2 ? prize.image2.image : "",
                  isStretch: prize.image2 ? prize.image2.isStretch : false,
                  description: prize.image2.description || "",
                  type: prize.image2.type || 'photo'
                },
                image3: {
                  image: prize.image3 ? prize.image3.image : "",
                  isStretch: prize.image3 ? prize.image3.isStretch : false,
                  description: prize.image3.description || "",
                  type: prize.image3.type || 'photo'
                },
                image4: {
                  image: prize.image4 ? prize.image4.image : "",
                  isStretch: prize.image4 ? prize.image4.isStretch : false,
                  description: prize.image4.description || "",
                  type: prize.image4.type || 'photo'
                },
                image5: {
                  image: prize.image5 ? prize.image5.image : "",
                  isStretch: prize.image5 ? prize.image5.isStretch : false,
                  description: prize.image5.description || "",
                  type: prize.image5.type || 'photo'
                },
                image6: {
                  image: prize.image6 ? prize.image6.image : "",
                  isStretch: prize.image6 ? prize.image6.isStretch : false,
                  description: prize.image6.description || "",
                  type: prize.image6.type || 'photo'
                },
                position: prize.position,
                type: prize.type,
                id: prize.id,
              };
              // Add media if present
              if (prize.media) {
                this.gameSettingsService.autoSelectPrizes[prize.position].media = prize.media;
              }
            }

            this.settingPrizeConfetti(prize);
          }
        });
      }

      if (game.grid) {
        this.gridType = game.grid.noOfColoumns;
        this.emojisPerScreen =
          this.gridType === "4" ? 16 : this.gridType === "2" ? 6 : 48;
        if (this.gridType === "2") {
          this.noOfFormControls.length = 48;
          this.noOfTabs = [
            { screenNo: "Screen 1" },
            { screenNo: "Screen 2" },
            { screenNo: "Screen 3" },
            { screenNo: "Screen 4" },
            { screenNo: "Screen 5" },
            { screenNo: "Screen 6" },
            { screenNo: "Screen 7" },
            { screenNo: "Screen 8" },
          ];
        } else if (this.gridType === "6") {
          this.noOfFormControls.length = 48;
          this.noOfTabs = [{ screenNo: "Screen" }];
        }
        for (let i = 0; i < this.noOfFormControls.length; i++) {
          const control = <FormArray>this.tabForm.controls["popUp"];
          control.push(this.gameSettingsService.creatingPopUpFormControls(i));
        }
      }

      if (game.imageWithTextContent && (game.imageWithTextContent.original || game.imageWithTextContent.img)) {
        this.rule = "imageWithText";
        if (game.imageWithTextContent.isStretch !== undefined) {
          this.imageRuleStretch = game.imageWithTextContent.isStretch;
        }
        this.verticalStepperStep1.patchValue({
          rule: "imageWithText",
          ruleTextRange: game.ruleTextRange,
        });
      } else if (game.embedWithTextContent && (game.embedWithTextContent.original || game.embedWithTextContent.embededVideoRules)) {
        this.rule = "embedWithText";
        this.verticalStepperStep1.patchValue({
          rule: "embedWithText",
          ruleTextRange: game.ruleTextRange,
        });
      } else if (game.imageRule && game.imageRule.img) {
        this.rule = "image";
        if (game.imageRule.isStretch !== undefined) {
          this.imageRuleStretch = game.imageRule.isStretch;
        }
        this.verticalStepperStep1.patchValue({
          rule: "image",
          ruleTextRange: game.ruleTextRange,
        });
      } else if (game.embededVideoRules) {
        this.rule = "embed";
        this.verticalStepperStep1.patchValue({
          rule: "embed",
          ruleTextRange: game.ruleTextRange,
        });
      } else if (game.textRules) {
        this.rule = "text";
        this.verticalStepperStep1.patchValue({
          rule: "text",
          ruleTextRange: game.ruleTextRange,
        });
      }

      this.verticalStepperStep1.patchValue({
        isPrivate: game.isPrivate !== undefined ? game.isPrivate : false,
        title: game.title,
        value: game.value,
        categoryId: game.categoryId,
        drawDate: game.drawDate,
        sharingContentCountdown: game.sharingContentCountdown,
        schedule: game.schedule ? game.schedule : null,
        isRestartable: game.isRestartable ? game.isRestartable : null,
        isRegistration: game.isRegistration,
        isLocation: game.isLocation,
        isLocationProximity: game.isLocationProximity,
        restartThreshold: game.restartThreshold || null,
        cycles: game.cycles || null,
      });

      if (game.categoryId)
        await this.gameSettingsService.onCategorySelectChange(
          game.categoryId,
          game.id || null
        );

      if (this.queryParams._value.action === "restart") {
        this.drawDateFlag = true;
        this.verticalStepperStep1.patchValue({
          drawDate: "",
          sharingContentCountdown: null,
        });
      }
      this.verticalStepperStep5.patchValue({
        inviteImageArtTitle: "",
        inviteImageUrl: "",
        isSwipeMenu: game.isSwipeMenu ? game.isSwipeMenu : false,
        surveyOnly: game && game.surveyOnly ? game.surveyOnly : false,
      });

      this.verticalStepperStep2.patchValue({
        gridType: game.gridType,
        isRounded: game.isRounded,
        submittedEmojiSmallFormat: game.submittedEmojiSmallFormat,
        gamePlayEmojiBackground: game.gamePlayEmojiBackground,
        gameplayEmojiPosition: game.gameplayEmojiPosition || "top",
        isFill: game.isFill,
        isCompressed: game.isCompressed,
      });

      let embedVideoUrl = game.embededVideoRules || "";
      if (game.embedWithTextContent && game.embedWithTextContent.embededVideoRules) {
        embedVideoUrl = game.embedWithTextContent.embededVideoRules;
      }

      let imgRuleUrl = game.imageRule && game.imageRule.img ? game.imageRule.img : "";
      if (game.imageWithTextContent && game.imageWithTextContent.img) {
        imgRuleUrl = game.imageWithTextContent.img;
      }

      this.verticalStepperStep1.patchValue({
        textRules: game.textRules || "",
        embededVideoRules: embedVideoUrl,
        imageRule: imgRuleUrl,
        confirmationText: game.confirmationText && game.confirmationText.original ? game.confirmationText : this.getDefaultConfirmationText(),
        embedWithTextContent: game.embedWithTextContent && (game.embedWithTextContent.original || game.embedWithTextContent.embededVideoRules) ? game.embedWithTextContent : { original: "", translations: {} },
        imageWithTextContent: game.imageWithTextContent && (game.imageWithTextContent.original || game.imageWithTextContent.img) ? game.imageWithTextContent : { original: "", translations: {} }
      });
      this.gameSettingsService
        .getSelectedPlayersData(this.game.selectedPlayers)
        .then((players) => {
          this.game.selectedPlayers = players;
        });

      const popupArray = this.tabForm.getRawValue();
      this.gameSettingsService
        .getGameEmojis(gameId)
        .then((gameEmojis: any[]) => {
          if (gameEmojis) {
            gameEmojis.forEach((emoji, index) => {
              if (popupArray.popUp[index]) {
                popupArray.popUp[index]["adType" + index] =
                  emoji.popup && emoji.popup.type ? emoji.popup.type : "ad";
                popupArray.popUp[index]["contentTabType" + index] =
                  emoji.popup && emoji.popup.content && emoji.popup.content.type
                    ? emoji.popup.content.type
                    : "CONTENT";
                popupArray.popUp[index]["sfxSound" + index] = emoji.sfx
                  ? emoji.sfx
                  : "sfxSound";
                popupArray.popUp[index]["popUp" + index] = emoji.popup;
                popupArray.popUp[index]["countDown" + index] =
                  emoji.popup.timer;
                if (emoji.popup.timerData) {
                  popupArray.popUp[index]["countDownImage" + index] =
                    emoji.popup.timerData.img;
                  popupArray.popUp[index]["countDownImageIsExpand" + index] =
                    emoji.popup.timerData.isExpand;
                  popupArray.popUp[index]["countDownEmoji" + index] =
                    emoji.popup.timerData.emojis;
                  popupArray.popUp[index]["countDownTextAreaInput" + index] =
                    emoji.popup.timerData.textAreaCountDownInput;
                }
              }
            });
          }
          if (_.isEqual(this.gridType, "4")) {
            this.gameSettingsService.screensEmojis = [
              ...this.gameSettingsService.gameEmojis,
            ];

            this.tabForm.patchValue({ popUp: popupArray.popUp });
          } else {
            this.tabForm.patchValue({ popUp: popupArray.popUp });
          }
        });
    }

    this.cdref.detectChanges();
  };

  getWidth(i: number, prizeMode?: string) {
    return this.prizeForm.value.prize[i]["type" + i] === "solo" ? "293" : "155";
  }

  getSmallScreenWidth(i: number, prizeMode?: string) {
    return this.prizeForm.value.prize[i]["type" + i] === "solo" ? "200" : "155";
  }

  isContent(index) {
    return (
      this.tabForm.value.popUp[index]["adType" + index] === "AD" ||
      (this.tabForm.value.popUp[index]["adType" + index] !== "MANAD" &&
        this.tabForm.value.popUp[index]["adType" + index] !== "ADMOB")
    );
  }

  getPopUpControl(index) {
    return {
      popup: this.tabForm.value.popUp[index],
      index: index,
      gameCategoryId: this.verticalStepperStep1.value.categoryId,
      ques: this.gameSettingsService.contents,
    };
  }

  /**
   * check form values changes from game object
   * @param index
   */
  onStepChange(index: number): void {
    if (this.queryParams._value.action === "restart" && index !== 0) {
      const object1 = this.game;
      const object2 = this[`verticalStepperStep${index}`].getRawValue();

      // todo: popup isDuplicationFlag true updation will be handle after
      // todo: phase 7 popup functionality is added.

      // Find matching keys and check for value changes
      const matchingKeysWithChanges = [];
      const keys1 = Object.keys(this.game);

      keys1.forEach((key) => {
        if (object2.hasOwnProperty(key)) {
          let flag =
            key !== "gameMenuArt" &&
            key !== "gameMenuLayer" &&
            key !== "logo" &&
            key !== "powerBar" &&
            key !== "imageRule" &&
            key !== "drawDate" &&
            key !== "sharingContentCountdown";

          if (flag && object1[key] !== object2[key]) {
            matchingKeysWithChanges.push({
              key,
              oldValue: object1[key],
              newValue: object2[key],
            });
          }
        }
      });

      // * setting isDuplication to true
      if (matchingKeysWithChanges.length !== 0)
        this.gameSettingsService.isDuplicationFlag = true;
    }
  }

  selectedRuleType(ruleType: string) {
    this.rule = ruleType;

    // Helper to reset a control
    const resetControl = (controlName: string) => {
      const control = this.verticalStepperStep1.get(controlName);
      if (control) {
        const patchObj: any = {};
        patchObj[controlName] = "";
        this.verticalStepperStep1.patchValue(patchObj);

        control.setValidators(null);
        control.updateValueAndValidity();
      }
    };

    // First reset all rules
    resetControl("textRules");
    resetControl("embededVideoRules");
    resetControl("imageRule");

    this.imageRuleStretch = false;

    // Apply validator only on the selected rule type
    switch (ruleType) {
      case "text":
        const textCtrl = this.verticalStepperStep1.get("textRules");
        if (textCtrl) {
          textCtrl.setValidators([Validators.required]);
          textCtrl.updateValueAndValidity();
        }
        break;

      case "embed":
      case "embedWithText":
        const embedCtrl = this.verticalStepperStep1.get("embededVideoRules");
        if (embedCtrl) {
          embedCtrl.setValidators([Validators.required]);
          embedCtrl.updateValueAndValidity();
        }
        break;

      case "image":
      case "imageWithText":
        const imageCtrl = this.verticalStepperStep1.get("imageRule");
        if (imageCtrl) {
          imageCtrl.setValidators([Validators.required]);
          imageCtrl.updateValueAndValidity();
        }
        break;

      default:
        this.rule = "";
        break;
    }

    // Store rule in localStorage if id is undefined
    if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
      localStorage.setItem("rule", this.rule);
    }
  }

  /**
   *
   * @param action
   * @param gameId
   * @param game
   */
  gatheringPrizeData(action, gameId?, game?: Game) {
    let prizeArray = [];
    const prizeForm = this.prizeForm.getRawValue();

    prizeForm.prize.forEach((prize, index) => {
      if (prize["type" + index]) {
        if (
          this.gameSettingsService.autoSelectPrizes[index] &&
          prize["type" + index] ===
          this.gameSettingsService.autoSelectPrizes[index].type &&
          this.game &&
          prizeButtons.map((el) => el.type).includes(this.game.prizeMode)
        ) {
          let prizeData: any = {
            type: this.gameSettingsService.autoSelectPrizes[index].type,
            image1: {
              ...this.gameSettingsService.autoSelectPrizes[index].image1,
            },
            title: this.gameSettingsService.autoSelectPrizes[index].title,
            description:
              this.gameSettingsService.autoSelectPrizes[index].description,
            buyNowUrl:
              this.gameSettingsService.autoSelectPrizes[index].buyNowUrl ||
              null,
            position: this.gameSettingsService.autoSelectPrizes[index].position,
            cashPrizeValue: "",
            winPattern: [],
            gameId: gameId,
          };

          // Add media only if it's not empty
          if (
            this.gameSettingsService.autoSelectPrizes[index].media &&
            (
              (this.gameSettingsService.autoSelectPrizes[index].media.image &&
                this.gameSettingsService.autoSelectPrizes[index].media.image.image) ||
              this.gameSettingsService.autoSelectPrizes[index].media.video
            )
          ) {
            prizeData.media = _.cloneDeep(this.gameSettingsService.autoSelectPrizes[index].media);
          }

          // Additional properties for specific confetti types
          const confettiConfig = this.gameSettingsService.prizeConfetti ? this.gameSettingsService.prizeConfetti[index] : null;

          if (confettiConfig) {
            if (confettiConfig.type === "color") {
              prizeData.prizeConfetti = confettiConfig.color;
            } else if (confettiConfig.type === "emoji") {
              prizeData.prizeConfetti = confettiConfig.emoji;
            } else {
              prizeData.prizeConfetti = confettiConfig.canvas;
            }
          }

          if (
            prize["type" + index] === "solo" ||
            prize["type" + index] === "auto select"
          ) {
            for (let i = 2; i <= 6; i++) {
              if (
                this.gameSettingsService.autoSelectPrizes[index]["image" + i]
              ) {
                prizeData["image" + i] = {
                  ...this.gameSettingsService.autoSelectPrizes[index][
                  "image" + i
                  ],
                };
              }
            }
          }

          // Update image URLs
          for (let i = 1; i <= 6; i++) {
            if (prizeData["image" + i] && prizeData["image" + i].image) {
              prizeData["image" + i].image = prizeData[
                "image" + i
              ].image.replace(this.s3BucketUrl, "");
            }
          }

          // Update Media Image URL
          if (
            prizeData.media &&
            prizeData.media.image &&
            prizeData.media.image.image
          ) {
            prizeData.media.image.image = prizeData.media.image.image.replace(
              this.s3BucketUrl,
              ""
            );
          }

          // Update prizeData id
          if (
            this.editedGameId &&
            this.gameSettingsService.autoSelectPrizes[index] &&
            this.gameSettingsService.autoSelectPrizes[index].id
          ) {
            prizeData.id = this.gameSettingsService.autoSelectPrizes[index].id;
          }

          prizeArray.push(prizeData);
        }

        let winPattern = this.gameSettingsService.winPattern;

        if (
          winPattern[index] &&
          prize["type" + index] === winPattern[index].type
        ) {
          let prizeData: any = {
            type: winPattern[index].type,
            winPattern: winPattern[index].cashPrizeImages,
            cashPrizeValue: winPattern[index].cashPrizeValue,
            prizeConfetti: winPattern[index].prizeConfetti,
            position: winPattern[index].index,
            gameId: gameId,
          };

          if (
            winPattern[index] &&
            winPattern[index].image1 &&
            winPattern[index].image1.image
          ) {
            prizeData.image1 = {
              image: winPattern[index].image1.image,
              isStretch: false,
              description: winPattern[index].image1.description || "",
            };
          }

          if (this.editedGameId && this.game && this.game.prizes && this.game.prizes[index]) {
            prizeData.id = this.game.prizes[index].id;
          }
          prizeArray.push(prizeData);
        }

        // Additional conditions...
      }
    });

    // Additional logic...

    if (action === "data") {
      prizeArray = this.concatinatingPrizes(prizeArray);
      return prizeArray;
    } else {
      if (this.exactMatchCheck.filter((exact) => !exact).length === 0) {
        this.prizeService.savePrizes(
          prizeArray,
          this.game && this.game.id ? this.game.id : ""
        );
      }
    }
  }

  concatinatingPrizes(prizeArray) {
    prizeArray.forEach((prize) => {
      if (!_.isUndefined(prize.image1) && !_.isUndefined(prize.image1.image)) {
        prize.image1.image =
          prize.image1.image !== ""
            ? this.s3BucketUrl + prize.image1.image
            : "";
      }
      if (!_.isUndefined(prize.image2) && !_.isUndefined(prize.image2.image)) {
        prize.image2.image =
          prize.image2.image !== ""
            ? this.s3BucketUrl + prize.image2.image
            : "";
      }
      if (!_.isUndefined(prize.image3) && !_.isUndefined(prize.image3.image)) {
        prize.image3.image =
          prize.image3.image !== ""
            ? this.s3BucketUrl + prize.image3.image
            : "";
      }
      if (!_.isUndefined(prize.image4) && !_.isUndefined(prize.image4.image)) {
        prize.image4.image =
          prize.image4.image !== ""
            ? this.s3BucketUrl + prize.image4.image
            : "";
      }
      if (!_.isUndefined(prize.image5) && !_.isUndefined(prize.image5.image)) {
        prize.image5.image =
          prize.image5.image !== ""
            ? this.s3BucketUrl + prize.image5.image
            : "";
      }
      if (!_.isUndefined(prize.image6) && !_.isUndefined(prize.image6.image)) {
        prize.image6.image =
          prize.image6.image !== ""
            ? this.s3BucketUrl + prize.image6.image
            : "";
      }
      if (
        prize.media &&
        prize.media.image &&
        prize.media.image.image &&
        prize.media.image.image !== ""
      ) {
        prize.media.image.image = this.s3BucketUrl + prize.media.image.image;
      }
    });
    return prizeArray;
  }

  helperFunction() {
    const prizeCount = this.getNumberOfPrizes();
    if (this.game.prizeMode === "auto select mode" && prizeCount > 0) {
      return this.gatheringPrizeData("data", this.editedGameId, this.game);
    }
    if (
      prizeButtons.map((el) => el.type).includes(this.game.prizeMode) &&
      prizeCount > 1
    ) {
      return this.gatheringPrizeData("data", this.editedGameId, this.game);
    }
  }

  getColorWheelData() {
    const { type, data } = this.gameSettingsService.colorWheel;
    let colorWheel: any = {};

    if (type === "img" && data.img) {
      colorWheel = {
        type,
        cssCenterChoice: data.cssCenterChoice,
        img:
          data.img && data.img.file && data.img.file !== null
            ? data.img.file
            : data.img.src,
      };
    } else if (type === "java") {
      const { color_1, color_2, color_3, color_4, color_5, color_6 } = data;
      colorWheel = {
        type,
        cssCenterChoice: data.cssCenterChoice,
        colors: [color_1, color_2, color_3, color_4, color_5, color_6],
      };
    } else if (type === "css") {
      const { color_1, color_2, color_3, color_4, color_5, color_6 } = data;
      colorWheel = {
        type,
        cssCenterChoice: data.cssCenterChoice,
        colors: [color_1, color_2, color_3, color_4, color_5, color_6],
      };
    }

    return colorWheel;
  }

  colorWheelUpload = async ($event) => {
    const uploadPath =
      environment.mainContainer + "/" + environment.colorWheelPath;
    try {
      let response: any = await this.gameSettingsService
        .uploadImage($event, uploadPath, this.adminApi.getCurrentToken().id)
        .toPromise();
      if (!response) {
        throw new Error(
          "Cannot Upload Color Wheel Loader Image, Please Try Again!"
        );
      }
      this.gameSettingsService.colorWheel.data.img.src =
        response.result.files["file"][0]["providerResponse"].name;
      this.gameSettingsService.colorWheel.data.img.file = null;
    } catch (error) {
      this.fuseSplashScreen.hide();
      throw Error(error.message);
    }
  };

  deletePrizes = async () => {
    try {
      const response = await this.gameSettingsService
        .deletePrizes()
        .toPromise();
      this.game.prizes = [];
      this.game.prizes = this.helperFunction();
    } catch (error) {
      this.fuseSplashScreen.hide();
      throw Error(error.message);
    }
  };

  clubInvitesChange(_clubInvites: ClubInvite[]) {
    this.clubInvites = _clubInvites;
    // console.log("clubInvitesChange", this.clubInvites);
  }

  gameUpdate = async () => {
    // this.fuseSplashScreen.show();

    // * uploading color wheel loader if file present
    const colorWheelType = this.gameSettingsService.colorWheel.type;
    const colorWheelData = this.gameSettingsService.colorWheel.data;

    if (colorWheelType === "img" && colorWheelData.img.file !== null) {
      if (colorWheelData && colorWheelData.img && colorWheelData.img.file) {
        await this.colorWheelUpload(colorWheelData.img.file);
      } else {
        this.fuseSplashScreen.hide();
        this.snackBar.open("Please Provide Color Wheel Loader", "Error", {
          verticalPosition: "top",
          duration: 3000,
        });
        throw new Error("Please Provide Color Wheel Loader");
      }
    }

    let emojisFlag = true;

    if (
      this.gameSettingsService.game.prizeMode !== this.game.prizeMode &&
      this.queryParams._value.action !== "restart"
    )
      await this.deletePrizes();

    if (this.gameSettingsService.game.prizeMode === this.game.prizeMode) {
      this.game.prizes = this.helperFunction();
    }

    const prizeCount = this.getNumberOfPrizes();

    const drawDate = this.verticalStepperStep1.value.drawDate;
    const logoUrl = this.gameLogo.split("/");
    const artUrl = this.menuArt.split("/");

    const data: any = {
      adminId: this.adminApi.getCurrentId(),
      title: this.verticalStepperStep1.value.title,
      confirmationText: this.verticalStepperStep1.value.confirmationText,
      value: this.verticalStepperStep1.value.value,
      categoryId: this.verticalStepperStep1.value.categoryId,
      drawDate: moment(drawDate).seconds(0).milliseconds(0).toJSON(),
      sharingContentCountdown:
        this.verticalStepperStep1.value.sharingContentCountdown !== ""
          ? this.verticalStepperStep1.value.sharingContentCountdown
          : null,
      schedule: this.verticalStepperStep1.value.schedule,
      isRestartable: this.verticalStepperStep1.value.schedule ? true : false,
      isRegistration: this.verticalStepperStep1.value.isRegistration,
      isLocation: this.verticalStepperStep1.value.isLocation,
      isLocationProximity: this.verticalStepperStep1.value.isLocation
        ? this.verticalStepperStep1.value.isLocationProximity
        : false,
      prizeMode: this.game.prizeMode,
      invites:
        this.invitesData.invites.filter(
          (el) => el.image !== "" || el.inviteImageUrl !== ""
        ).length !== 0
          ? this.invitesData.invites
          : [],
      powerBar: {
        image: this.invitesData.powerBar.image,
        audio: this.invitesData.powerBar.audio,
        isExpand: this.invitesData.powerBar.isExpand,
      },
      inviteType: this.inviteType,
      isInviteImageArtGradient: this.invitesData.isInviteImageArtGradient,
      isProgressBarTransparent: this.invitesData.isProgressBarTransparent,
      isGameMenuToolbar: this.gameSettingsService.isGameMenuToolbar,
      gameType: "edited",
      isPrivate: this.verticalStepperStep1.value.isPrivate,
      selectedPlayers: this.game.selectedPlayers,
      gridType: this.verticalStepperStep2.value.gridType,
      isRounded: this.verticalStepperStep2.value.isRounded,
      submittedEmojiSmallFormat: this.verticalStepperStep2.value.submittedEmojiSmallFormat,
      gamePlayEmojiBackground: this.verticalStepperStep2.value.gamePlayEmojiBackground,
      gameplayEmojiPosition: this.verticalStepperStep2.value.gameplayEmojiPosition,
      isFill: this.verticalStepperStep2.value.isFill,
      isCompressed: this.verticalStepperStep2.value.isCompressed,
      isSwipeMenu: this.verticalStepperStep5.value.isSwipeMenu,
      colorWheel: this.getColorWheelData(),
      tieBreakerColorWheel: this.gameSettingsService.tieBreakerColorWheel,
      surveyOnly: this.verticalStepperStep5.value.surveyOnly
    };

    if (this.dummyAssetsData.header && this.dummyAssetsData.header.image) {
      data.gameWinLosePreview = {
        header: this.dummyAssetsData.header,
      };
    }

    // Add prizeBackground if it has an image
    if (this.dummyAssetsData.prizeBackground && this.dummyAssetsData.prizeBackground.image) {
      if (!data.gameWinLosePreview) {
        data.gameWinLosePreview = {};
      }
      data.gameWinLosePreview.prizeBackground = this.dummyAssetsData.prizeBackground;
    }

    // Add buttonBackground if opacity is set
    if (this.dummyAssetsData.buttonBackground && this.dummyAssetsData.buttonBackground.opacity !== undefined) {
      if (!data.gameWinLosePreview) {
        data.gameWinLosePreview = {};
      }
      data.gameWinLosePreview.buttonBackground = this.dummyAssetsData.buttonBackground;
    }

    if (this.gameTicket && this.gameTicket.image) {
      data.gameTicket = this.gameTicket;
    }

    if (data.isRegistration) {
      data.registrationForm = this.game && this.game.registrationForm ? this.game.registrationForm : null;
    } else if (!data.isRegistration && this.game.registrationForm) {
      data.registrationForm = null;
    }

    if (this.queryParams && this.queryParams._value.action === "restart") {
      const createdAt = moment().seconds(0).milliseconds(0).utc();
      data.timeDifference = moment(drawDate).diff(moment(createdAt), "seconds");
    }

    if (this.gameSettingsService.selectedSimilarGames.length !== 0) {
      data.similarGames = [...this.gameSettingsService.selectedSimilarGames];
    } else {
      data.similarGames = [];
    }

    if (data.isLocation) {
      data.notifyLocation = {
        lat: this.gameSettingsService.latitude,
        lng: this.gameSettingsService.longitude,
      };
      data.locationRadius = this.gameSettingsService.radius;
      data.locationRadiusUnit = this.gameSettingsService.unit;
    } else {
      data.notifyLocation = {
        lat: 0,
        lng: 0,
      };
      data.locationRadius = null;
      data.locationRadiusUnit = null;
    }
    data.restartThreshold =
      data.schedule !== "none"
        ? this.verticalStepperStep1.value.restartThreshold
        : null;

    data.cycles =
      data.schedule !== "instant" && data.schedule !== "none"
        ? this.verticalStepperStep1.value.cycles
        : null;

    if (this.drawDateFlag) {
      data.gameType = "restarted";
      data.restartedBy = "admin";
    }
    if (this.rule === "text") {
      data.textRules = this.verticalStepperStep1.value.textRules;
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.embededVideoRules = "";
      data.imageRule = {};
      data.embedWithTextContent = null;
      data.imageWithTextContent = null;
    } else if (this.rule === "embed") {
      data.embededVideoRules =
        this.verticalStepperStep1.value.embededVideoRules;
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {};
      data.embedWithTextContent = null;
      data.imageWithTextContent = null;
    } else if (this.rule === "image") {
      data.embededVideoRules = "";
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {
        img: this.verticalStepperStep1.value.imageRule
          ? this.verticalStepperStep1.value.imageRule.replace(this.s3BucketUrl, "")
          : "",
        isStretch: this.imageRuleStretch,
      };
      data.embedWithTextContent = null;
      data.imageWithTextContent = null;
    } else if (this.rule === "embedWithText") {
      data.embededVideoRules = "";
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {};
      const embedContent = this.verticalStepperStep1.value.embedWithTextContent || {};
      data.embedWithTextContent = {
        embededVideoRules: this.verticalStepperStep1.value.embededVideoRules || "",
        original: embedContent.original || "",
        translations: embedContent.translations || {},
      };
      data.imageWithTextContent = null;
    } else if (this.rule === "imageWithText") {
      data.embededVideoRules = "";
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {};
      const imgContent = this.verticalStepperStep1.value.imageWithTextContent || {};
      let rawImg = this.verticalStepperStep1.value.imageRule || "";
      if (rawImg) {
        rawImg = rawImg.replace(this.s3BucketUrl, "");
      }
      data.imageWithTextContent = {
        img: rawImg,
        isStretch: this.imageRuleStretch,
        original: imgContent.original || "",
        translations: imgContent.translations || {},
      };
      data.embedWithTextContent = null;
    } else if (this.rule === "") {
      data.embededVideoRules = "";
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {};
      data.embedWithTextContent = null;
      data.imageWithTextContent = null;
    }
    if ("https:" !== logoUrl[0]) {
      data.logo = {
        image: this.gameLogo,
        isStretch: this.logoStretch,
      };
    } else {
      this.gameLogo = this.gameLogo.replace(environment.s3BucketUrl, "");
      data.logo = {
        image: this.gameLogo,
        isStretch: this.logoStretch,
      };
    }

    // menu art image
    if ("https:" !== artUrl[0]) {
      data.gameMenuArt = {
        image: this.menuArt,
        imageScale: this.imageScale,
        menuArtOption: this.manuArtSizeNavigation,
      };
    } else {
      this.menuArt = this.menuArt.replace(environment.s3BucketUrl, "");
      data.gameMenuArt = {
        image: this.menuArt,
        imageScale: this.imageScale,
        menuArtOption: this.manuArtSizeNavigation,
      };
    }

    // layer image
    if (this.layer) {
      const layerUrl = this.layer.split("/");

      if (layerUrl[0] === "https:") {
        this.layer = this.layer.replace(environment.s3BucketUrl, "");
      }

      data.gameMenuLayer = {
        image: this.layer,
        imageScale: this.layerImageScale,
        showInGameplay: this.showInGameplay,
      };
    }

    if (this.automatchButtonArt) {
      const automatchUrl = this.automatchButtonArt.split("/");
      if (automatchUrl[0] === "https:") {
        this.automatchButtonArt = this.automatchButtonArt.replace(environment.s3BucketUrl, "");
      }
      data.automatchButtonArt = {
        image: this.automatchButtonArt
      };
    } else {
      data.automatchButtonArt = null;
    }

    if (this.selfPickButtonArt) {
      const selfPickUrl = this.selfPickButtonArt.split("/");
      if (selfPickUrl[0] === "https:") {
        this.selfPickButtonArt = this.selfPickButtonArt.replace(environment.s3BucketUrl, "");
      }
      data.selfPickButtonArt = {
        image: this.selfPickButtonArt
      };
    } else {
      data.selfPickButtonArt = null;
    }

    // FINAL X p28-36. gameFormat picks the gameplay layout; dressUpGroups/
    // dressUpFigure only matter when gameFormat === 'dressUp' (server-side
    // validation in Game.validate('dressUpGroups', ...) is a no-op
    // otherwise), but are still sent so switching away from Dress Up doesn't
    // silently discard the admin's in-progress group assignments.
    if (!this.syncDressUpGroupsFromScreens()) return;
    data.gameFormat = this.verticalStepperStep1.value.gameFormat || "standard";
    data.dressUpGroups = this.dressUpGroups;
    if (this.dressUpFigure) {
      const figureUrl = this.dressUpFigure.split("/");
      if (figureUrl[0] === "https:") {
        this.dressUpFigure = this.dressUpFigure.replace(environment.s3BucketUrl, "");
      }
      data.dressUpFigure = { image: this.dressUpFigure };
    } else {
      data.dressUpFigure = null;
    }

    data.titleOpt = this.titleOpt;
    data.shadowOpt = this.shadowOpt;
    data.categoryOpt = this.categoryOpt;
    data.enterFreeOpt = this.enterFreeOpt;
    data.seatMapOpt = this.seatMapOpt;

    if (this.inviteType === "color-gradient" || this.inviteType === "layer") {
      data.gameMenuArt.firstColor = this.menuArt1stColor;
      data.gameMenuArt.secondColor = this.menuArt2ndColor;
    }
    if (this.inviteArt) {
      const inviteArtUrl = this.inviteArt.split("/");
      data.inviteImageArt.image = inviteArtUrl[3] + "/" + inviteArtUrl[4];
      if ("https:" !== inviteArtUrl[0]) {
        data.inviteImageArt.image = this.inviteArt;
      }
    }
    if (this.gridType === "4") {
      data.grid = {
        noOfColoumns: "4",
        itemsinGrid: "16",
        noOfGrids: "3",
      };
      if (this.gameSettingsService.extraEmojisSet.length !== 48) {
        this.fuseSplashScreen.hide();
        this.snackBar.open("Please keep 16 emoji in all three screens", "OK", {
          verticalPosition: "top",
          duration: 3000,
        });
        emojisFlag = false;
        throw new Error("Please keep 16 emoji in all three screens");
      }
    } else if (this.gridType === "2") {
      data.grid = {
        noOfColoumns: "2",
        itemsinGrid: "6",
        noOfGrids: "8",
      };
      if (this.gameSettingsService.extraEmojisSet.length !== 48) {
        this.fuseSplashScreen.hide();
        this.snackBar.open("Please keep 6 emoji in all six screens", "OK", {
          verticalPosition: "top",
          duration: 3000,
        });
        emojisFlag = false;
        throw new Error("Please keep 6 emoji in all six screens");
      }
    } else if (this.gridType === "6") {
      data.grid = {
        noOfColoumns: "6",
        itemsinGrid: "48",
        noOfGrids: "1",
      };
      if (this.gameSettingsService.extraEmojisSet.length !== 48) {
        this.fuseSplashScreen.hide();
        this.snackBar.open("Please keep 48 emojis in screen", "OK", {
          verticalPosition: "top",
          duration: 3000,
        });
        emojisFlag = false;
        throw new Error("Please keep 48 emojis in screen");
      }
    }

    this.fuseSplashScreen.hide();

    const updatedRewards = await this.gatherAndUploadRewards();

    // console.log('data...', prizeCount)

    if (
      this.queryParams &&
      (this.queryParams.value.action === "edit" ||
        this.queryParams.value.action === "restart")
    )
      await this.gameUpdating(data, prizeCount, emojisFlag, updatedRewards);
  };

  gatherAndUploadRewards = async () => {
    const rewards = this.clubInvites.reduce(
      (acc, invite) => (invite.reward ? [...acc, invite.reward] : acc),
      []
    );

    return await this.gameSettingsService.uploadRewardImages(
      rewards,
      this.gameSettingsService.rewardUploadPath,
      this.adminApi.getCurrentToken().id
    );
  }

  gameUpdating = async (data, prizeCount, emojisFlag, rewards) => {
    this.fuseSplashScreen.show();
    let update = true;
    if (data.isPrivate) {
      data.selectedPlayers = data.selectedPlayers.map((player) =>
        player.id ? player.id : player
      );
    }
    if (data.isPrivate && data.selectedPlayers.length === 0) {
      this.fuseSplashScreen.hide();
      this.snackBar.open("Please Select one or more Players", "OK", {
        verticalPosition: "top",
        duration: 3000,
      });
      update = false;
      throw new Error("Please Select one or more Players");
    } else if (data.prizeMode === "auto select mode" && prizeCount < 1) {
      this.fuseSplashScreen.hide();
      this.snackBar.open("Please Select at least one Prize", "OK", {
        verticalPosition: "top",
        duration: 3000,
      });
      update = false;
      throw new Error("Please Select at least one Prize");
    } else if (data.prizeMode !== "auto select mode" && prizeCount < 2) {
      this.fuseSplashScreen.hide();
      this.snackBar.open("Please Select at least 2 prizes", "OK", {
        verticalPosition: "top",
        duration: 3000,
      });
      update = false;
      throw new Error("Please Select at least 2 prizes");
    } else {
      if (data.prizeMode !== "auto select mode" && prizeCount > 1) {
        const prizeFormArray = this.prizeForm.getRawValue();

        // console.log('prizeFormArray...', prizeFormArray);
        prizeFormArray.prize.forEach((prize, index) => {
          // Check for 'type' property directly, falling back to 'type' + index if needed
          const prizeType = prize.type || prize["type" + index];
          const prizeValue = prize.cashPrizeValue || prize["cashPrizeValue" + index];

          if (
            prizeType === "exact match" &&
            (prizeValue === "" || prizeValue === undefined)
          ) {
            this.snackBar.open(
              "Exact match value is mandatory. Please fill the required field",
              "OK",
              {
                verticalPosition: "top",
                duration: 3000,
              }
            );
            update = false;
            this.fuseSplashScreen.hide();
            throw new Error(
              "Exact match value is mandatory. Please fill the required field"
            );
          }
        });
      }
      if (this.game.prizeMode === "auto select mode" && prizeCount > 0) {
        const prizeFormArray = this.prizeForm.getRawValue();
        prizeFormArray.prize.forEach((prize, index) => {
          // Check for 'type' property directly, falling back to 'type' + index if needed
          const prizeType = prize.type || prize["type" + index];
          const prizeValue = prize.cashPrizeValue || prize["cashPrizeValue" + index];

          if (
            prizeType === "exact match" &&
            (prizeValue === "" || prizeValue === undefined)
          ) {
            this.snackBar.open(
              "Exact match value is mandatory. Please fill the required field",
              "OK",
              {
                verticalPosition: "top",
                duration: 3000,
              }
            );
            update = false;
            this.fuseSplashScreen.hide();
            throw new Error(
              "Exact match value is mandatory. Please fill the required field"
            );
          }
        });
      }
      if (this.exactMatchCheck.filter((exact) => !exact).length !== 0) {
        update = false;
        this.fuseSplashScreen.hide();
        this.snackBar.open("Exact match must have 6 emojis", "OK", {
          verticalPosition: "top",
          duration: 3000,
        });
        throw new Error("exact match must have 6 emojis");
      }
    }

    if (update && emojisFlag) {
      this.updatingPopUpAndEmojis();

      data.id = this.editedGameId;

      // console.log('this.game.prizes...', this.game.prizes)

      let prizes = _.cloneDeep(
        this.transformingPrizeBeforeSaving(this.game.prizes)
      );

      // console.log('prizes...', prizes);

      let action =
        this.queryParams.value.action === "edit" ? "update" : "restart";

      await this.upsertGame(data, this.game.emojis, prizes, rewards, action);

      this.gameSettingsService.populated = false;
      this.fuseSplashScreen.hide();
      let titleCasePipe = new TitleCasePipe();
      this.snackBar.open(
        `Game ${titleCasePipe.transform(action)}ed successfully`,
        "SUCCESS",
        {
          verticalPosition: "top",
          duration: 3000,
        }
      );
      this.router.navigate(["/dashboard"]);
    }
  };

  upsertGame = async (data, emojis, prizes, rewards, action) => {
    try {
      const response: any = await this.gameSettingsService
        .upsertGame(data, emojis, prizes, rewards, action)
        .toPromise();
      if (!response) {
        throw new Error(`Cannot ${action} game Please Try Again!`);
      }
    } catch (error) {
      // ErrorHandler (shared/sdk/services/core/error.service.ts) unwraps the
      // HttpErrorResponse down to the loopback error body, so `error` here
      // is the ValidationError object (statusCode/name/message/details), not
      // a generic HTTP failure. `error.details.messages` carries the actual
      // per-field validator text (e.g. "a Dress Up game needs exactly 6
      // groups of 8 emojis (48 in total)") without the "Game instance is
      // not valid..." wrapper noise — prefer that when present, verified
      // against a live 422 response from POST /api/games with an incomplete
      // dressUpGroups payload.
      let message = `Cannot ${action} game Please Try Again!`;
      if (error && error.details && error.details.messages) {
        const fieldMessages: string[] = [].concat(
          ...Object.values(error.details.messages as { [key: string]: string[] })
        );
        if (fieldMessages.length) {
          message = fieldMessages.join(" ");
        }
      } else if (error && error.message) {
        message = error.message;
      }
      this.snackBar.open(message, "ERROR", {
        verticalPosition: "top",
        duration: 6000,
      });
      this.fuseSplashScreen.hide();
      throw Error(error.message);
    }
  };

  //TODO: move this function to helper service
  transformingPrizeBeforeSaving(prizes) {
    if (!prizes) return [];
    prizes.forEach((prize) => {
      for (let i = 0; i < 6; i++) {
        if (
          prize[`image${i + 1}`] &&
          prize[`image${i + 1}`].image !== undefined &&
          prize[`image${i + 1}`].image !== ""
        ) {
          prize[`image${i + 1}`].image = prize[`image${i + 1}`].image.replace(
            this.s3BucketUrl,
            ""
          );
        }
      }
      if (
        prize.media &&
        prize.media.image &&
        prize.media.image.image &&
        prize.media.image.image !== ""
      ) {
        prize.media.image.image = prize.media.image.image.replace(
          this.s3BucketUrl,
          ""
        );
      }

      if (prize.type === "exact match") {
        prize.winPattern = prize.winPattern.map((pattern) => ({
          id: pattern.id,
          position: pattern.position,
        }));
      }
    });
    return prizes;
  }

  updatingPopUpAndEmojis = async () => {
    const popupArray = _.cloneDeep(this.tabForm.getRawValue());
    let emojiData: any;
    const emojis = [...this.gameSettingsService.extraEmojisSet];
    this.game.emojis = this.game.emojis.length > 48 ? [] : this.game.emojis;
    if (emojis && emojis.length !== 0) {
      emojis.forEach((gameEmoji, index) => {
        emojiData = {
          emojiId: gameEmoji.emojiId,
          gameId: this.editedGameId,
          position: index + 1,
          sfx:
            popupArray.popUp[index] &&
              popupArray.popUp[index]["sfxSound" + index]
              ? popupArray.popUp[index]["sfxSound" + index]
              : "sfxSound",
        };

        if (gameEmoji.id) {
          emojiData.id = gameEmoji.id;
        }

        // if (gameEmoji.emoji) {
        //   emojiData.emoji = gameEmoji.emoji;
        // }

        if (popupArray.popUp[index]) {
          emojiData.popup = popupArray.popUp[index]["popUp" + index];
          emojiData.popup.timer = popupArray.popUp[index]["countDown" + index]
            ? popupArray.popUp[index]["countDown" + index]
            : 0;
        }

        emojiData.popup.timerData = {
          img: popupArray.popUp[index]["countDownImage" + index],
          isExpand: popupArray.popUp[index]["countDownImageIsExpand" + index],
          emojis: popupArray.popUp[index]["countDownEmoji" + index],
          textAreaCountDownInput:
            popupArray.popUp[index]["countDownTextAreaInput" + index],
        };

        // admod, manad to ad
        if (
          emojiData.popup &&
          emojiData.popup.type === "ad" &&
          (_.isEqual(emojiData.popup.content, {}) ||
            (_.isEqual(emojiData.popup.content.data, {}) && // if data is empty and type is also empty
              _.isEqual(emojiData.popup.content.type, "")))
        ) {
          emojiData.popup = { timer: 0 };
        }
        emojiData.popup = emojiData.popup ? emojiData.popup : { timer: 0 };
        if (_.isUndefined(this.game.emojis)) this.game.emojis = [];
        this.game.emojis[index] = emojiData;
      });
    }
  };

  mergingPopUpAndEmojis() {
    this.game.emojis = [...this.gameSettingsService.extraEmojisSet];
    const popupArray = _.cloneDeep(this.tabForm.getRawValue());
    let emojiData: any = {};
    // connecting Emoji's link with gameId
    this.game.emojis.forEach((emoji, index) => {
      emojiData = {
        emojiId: emoji.id,
        gameId: null,
        position: emoji.position ? emoji.position : index + 1,
        sfx: popupArray.popUp[index]["sfxSound" + index]
          ? popupArray.popUp[index]["sfxSound" + index]
          : "sfxSound",
      };

      emojiData.popup = popupArray.popUp[index]["popUp" + index];
      emojiData.popup.timer = popupArray.popUp[index]["countDown" + index]
        ? popupArray.popUp[index]["countDown" + index]
        : 0;

      emojiData.popup.timerData = {
        img: popupArray.popUp[index]["countDownImage" + index],
        isExpand: popupArray.popUp[index]["countDownImageIsExpand" + index],
        emojis: popupArray.popUp[index]["countDownEmoji" + index],
        textAreaCountDownInput:
          popupArray.popUp[index]["countDownTextAreaInput" + index],
      };

      // admod to manad and ad
      if (
        emojiData.popup.type === "ad" &&
        emojiData.popup.content.type === ""
      ) {
        emojiData.popup = { timer: 0 };
      }
      if (_.isUndefined(this.game.emojis)) this.game.emojis = [];
      this.game.emojis[index] = emojiData;
    });
  }

  /**
   *
   * @param prize
   * @param i
   */
  prizeDelete(prize, i) {
    this.confirmationDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: "Are you sure you want to Delete?",
      },
    });
    this.confirmationDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (result) => {
        if (!result) return;
        this.fuseSplashScreen.show();
        try {
          let response = await this.prizeService
            .deleteById(prize.id)
            .toPromise();
          if (!response) {
            this.fuseSplashScreen.hide();
            return;
          }
          this.snackBar.open("Prize deleted successfully", "OK", {
            verticalPosition: "top",
            duration: 2000,
          });
          this.gameSettingsService.autoSelectPrizes[i] = "";
          const prizeFormArray = this.prizeForm.getRawValue();
          const prizeArray = [];
          prizeFormArray.prize.forEach((res, index) => {
            if (res["type" + index] === "auto select" && index === i) {
              res["type" + index] = "";
            }
            prizeArray.push(res);
          });
          this.prizeForm.patchValue({ prize: prizeArray });
          this.fuseSplashScreen.hide();
        } catch (error) {
          console.error("error on prize deletion...", error);
          this.fuseSplashScreen.hide();
          throw new Error(error.message);
        }
      });
  }

  // ***********************************
  // * All uploading related Functions *
  // ***********************************

  /**
   * TODO: merge all image upload functions to a single function
   * @param $event
   */
  imageUpload(
    $event: any,
    type: "imageRules" | "gameLogo" | "gameMenuArt" | "gameMenuLayer" | "automatchButtonArt" | "selfPickButtonArt" | "dressUpFigure",
    shouldCompress: boolean = true
  ) {
    if (!$event) return;

    let file: File | null = null;

    try {
      // Check if the event comes from an input element with files
      if (
        $event.target &&
        $event.target.files &&
        $event.target.files.length > 0
      ) {
        file = $event.target.files[0];
      }
      // Check if a single file object is passed directly
      else if ($event) {
        file = $event;
      }

      if (!file) {
        console.warn("No file found in event:", $event);
        return;
      }
    } catch (error) {
      console.error("Error reading file from event:", error, $event);
      return;
    }

    this.fuseSplashScreen.show();

    const handleUpload = (blobToUpload: Blob) => {
      this.gameSettingsService
        .upload(
          blobToUpload,
          this.gameSettingsService[`${type}UploadPath`],
          this.adminApi.getCurrentToken().id
        )
        .then((response: any) => {
          const imageName =
            response.result.files["file"][0]["providerResponse"].name;
          const imageLocation =
            response.result.files["file"][0]["providerResponse"].location;

          if (type === "imageRules") {
            this.verticalStepperStep1.patchValue({ imageRule: imageName });
          } else if (type === "gameLogo") {
            this.gameLogo = imageName;
            this.form.patchValue({ logo: this.gameLogo });
            this.verticalStepperStep5.patchValue({ logo: this.gameLogo });
            this.gameLogoUrl = imageLocation + '?t=' + Date.now();
            if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
              localStorage.setItem("gameLogo", this.gameLogo);
              localStorage.setItem("gameLogoUrl", this.gameLogoUrl);
            }
          } else if (type === "gameMenuArt") {
            this.menuArt = imageName;
            this.form.patchValue({ gameMenuArt: this.menuArt });
            this.verticalStepperStep5.patchValue({ gameMenuArt: this.menuArt });
            this.menuArtUrl = imageLocation + '?t=' + Date.now();
            if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
              localStorage.setItem("menuArt", this.menuArt);
              localStorage.setItem("menuArtUrl", this.menuArtUrl);
            }
          } else if (type === "gameMenuLayer") {
            this.layer = imageName;
            this.form.patchValue({ gameMenuLayer: this.layer });
            this.verticalStepperStep5.patchValue({ gameMenuLayer: this.layer });
            this.layerUrl = imageLocation;
            if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
              localStorage.setItem("layer", this.layer);
              localStorage.setItem("layerUrl", this.layerUrl);
            }
          } else if (type === "automatchButtonArt") {
            this.automatchButtonArt = imageName;
            this.form.patchValue({ automatchButtonArt: this.automatchButtonArt });
            this.verticalStepperStep5.patchValue({ automatchButtonArt: this.automatchButtonArt });
            this.automatchButtonArtUrl = imageLocation;
            if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
              localStorage.setItem("automatchButtonArt", this.automatchButtonArt);
              localStorage.setItem("automatchButtonArtUrl", this.automatchButtonArtUrl);
            }
          } else if (type === "selfPickButtonArt") {
            this.selfPickButtonArt = imageName;
            this.form.patchValue({ selfPickButtonArt: this.selfPickButtonArt });
            this.verticalStepperStep5.patchValue({ selfPickButtonArt: this.selfPickButtonArt });
            this.selfPickButtonArtUrl = imageLocation;
            if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
              localStorage.setItem("selfPickButtonArt", this.selfPickButtonArt);
              localStorage.setItem("selfPickButtonArtUrl", this.selfPickButtonArtUrl);
            }
          } else if (type === "dressUpFigure") {
            this.dressUpFigure = imageName;
            this.dressUpFigureUrl = imageLocation + '?t=' + Date.now();
            if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
              localStorage.setItem("dressUpFigure", this.dressUpFigure);
              localStorage.setItem("dressUpFigureUrl", this.dressUpFigureUrl);
            }
          }

          this.fuseSplashScreen.hide();
        })
        .catch((error) => {
          console.error("Upload error:", error);
          this.fuseSplashScreen.hide();
        });
    };

    if (shouldCompress) {
      this.helperService
        .compressImage(file)
        .then((result: any) => {
          handleUpload(result.compressedBlob);
        })
        .catch((error: any) => {
          console.error("Compression error:", error);
          this.fuseSplashScreen.hide();
        });
    } else {
      handleUpload(file);
    }
  }

  /**
   * Handles the (imageRemoved) output from app-automatch-selfpick-art,
   * clearing the art/url pair identified by `type`.
   * @param type which button art was removed
   */
  onAutomatchSelfpickRemoved(type: "automatchButtonArt" | "selfPickButtonArt") {
    if (type === "automatchButtonArt") {
      this.automatchButtonArt = null;
      this.automatchButtonArtUrl = null;
    } else if (type === "selfPickButtonArt") {
      this.selfPickButtonArt = null;
      this.selfPickButtonArtUrl = null;
    }
  }

  resolvedDressUpFigureSrc(): string {
    if (this.dressUpFigureUrl) return this.dressUpFigureUrl;
    if (this.dressUpFigure) return this.s3BucketUrl + this.dressUpFigure;
    return this.placeholderImage || "/assets/images/image-not-found-150x150.png";
  }

  syncDressUpGroupsFromScreens(): boolean {
    if (this.verticalStepperStep1 && this.verticalStepperStep1.value.gameFormat === 'dressUp') {
      const bodyPartNames = ["hats", "glasses", "shirts", "pants", "shoes", "necklaces"];
      const emojis = (this.gameSettingsService.extraEmojisSet && this.gameSettingsService.extraEmojisSet.length > 0)
        ? this.gameSettingsService.extraEmojisSet
        : (this.game && this.game.emojis ? this.game.emojis : []);
      this.dressUpGroups = bodyPartNames.map((bodyPart, idx) => {
        const slice = emojis.slice(idx * 8, idx * 8 + 8);
        return {
          bodyPart,
          emojiIds: slice.map((e: any) => {
            if (!e) return '';
            if (typeof e === 'string') return e;
            return (
              e.id ||
              e._id ||
              e.emojiId ||
              (e.emoji && (e.emoji.id || e.emoji._id)) ||
              ''
            );
          }).filter((id: string) => !!id && id !== '[object Object]')
        };
      });

      // Safety-net: detect cross-group duplicates (covers corrupted/legacy data too)
      const allIds = this.dressUpGroups.reduce((acc, g) => acc.concat(g.emojiIds), [] as string[]);
      const uniqueIds = new Set(allIds.filter(id => id && id !== 'undefined'));
      if (uniqueIds.size < allIds.filter(id => id && id !== 'undefined').length) {
        if (this.verticalStepperStep2) {
          this.verticalStepperStep2.setErrors({ duplicateDressUpEmojis: true });
        }
        return false; // Signal: caller should abort save
      }

      // Clear any stale duplicate error now that groups are valid
      if (this.verticalStepperStep2 && this.verticalStepperStep2.errors && this.verticalStepperStep2.errors.duplicateDressUpEmojis) {
        const { duplicateDressUpEmojis, ...rest } = this.verticalStepperStep2.errors;
        this.verticalStepperStep2.setErrors(Object.keys(rest).length > 0 ? rest : null);
      }
    }
    return true; // No duplicates / not dressUp
  }

  /**
   * Handles the (figureRemoved) output from app-dress-up-config.
   */
  onDressUpFigureRemoved() {
    this.dressUpFigure = null;
    this.dressUpFigureUrl = null;
  }

  /**
   * Handles the (groupsChange) output from app-dress-up-config.
   */
  onDressUpGroupsChange(groups: DressUpGroup[]) {
    this.dressUpGroups = Array.isArray(groups) ? [...groups] : [];
    this.syncDressUpToExtraEmojisSet();
  }

  onGridImgError(event: Event, grid?: any) {
    const img = event.target as HTMLImageElement;
    if (img.getAttribute("src") === this.placeholderImage) return;
    img.src = this.placeholderImage;
  }

  /**
   *
   * @param $event
   */
  uploadImageRules($event) {
    if (this.verticalStepperStep1.value.imageRule) {
      this.verticalStepperStep1.value.imageRule.replace(this.s3BucketUrl, "");
    }
    this.imageUpload($event, "imageRules", false);
  }

  /**
   * @param $event
   * @param index
   */
  sfxUpload($event, index) {
    this.fuseSplashScreen.show();
    this.gameSettingsService
      .uploadSound(
        $event,
        this.gameSettingsService.sfxSoundUploadPath,
        this.adminApi.getCurrentToken().id
      )
      .then(
        (response: any) => {
          const popUpArray = this.tabForm.getRawValue();
          popUpArray.popUp[index]["sfxSound" + index] =
            this.s3BucketUrl +
            response.result.files["file"][0]["providerResponse"].name;
          this.tabForm.patchValue({ popUp: popUpArray.popUp });
          this.fuseSplashScreen.hide();
        },
        (error) => {
          this.fuseSplashScreen.hide();
        }
      );
  }

  // ***********************************
  // * All uploading related Functions *
  // ***********************************

  /**
   * @param flag
   * @param imageType
   * @param $event
   */
  toggleIsStretched(flag, imageType, $event?) {

    var splitData = [];
    this.gameSettingsService.isDuplicationFlag = true;

    if (imageType) {
      splitData = imageType.split("/");
    }

    // ✅ Normalize checked value
    var checked = false;

    if ($event && typeof $event === 'object' && $event.checked !== undefined) {
      checked = $event.checked;
    } else if (typeof $event === 'boolean') {
      checked = $event;
    }

    if (splitData[0] === "image-rules" || splitData[3] === "image-rules") {
      this.imageRuleStretch = checked;
      this.saveInLocalStorage("imageRuleStretch", this.imageRuleStretch);
      return checked;
    }

    if (splitData[0] === "game-logos" || splitData[3] === "game-logos") {
      this.logoStretch = checked;
      this.saveInLocalStorage("logoStretch", this.logoStretch);
      return checked;
    }

    if (
      splitData[0] === "invite-image-art" ||
      splitData[3] === "invite-image-art"
    ) {
      this.inviteArtStretch = checked;
      this.saveInLocalStorage("inviteArtStretch", this.inviteArtStretch);
      return checked;
    }

    if (flag) {
      return true;
    }

    return false;
  }


  // TODO: move this function to helper service
  saveInLocalStorage(key, value) {
    if (_.isUndefined(this.gameSettingsService.routeParams.id)) {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  // move this functon to helper service
  getNumberOfPrizes() {
    let prizeCount = 0;
    const prizes = this.prizeForm.getRawValue();
    prizes.prize.forEach((prize, index) => {
      if (
        (prize["type" + index] === "auto select" ||
          prize["type" + index] === "top 6") &&
        this.gameSettingsService.autoSelectPrizes.length !== 0 &&
        this.gameSettingsService.autoSelectPrizes.filter(
          (prize) => !_.isEqual(prize.title, "")
        ).length !== 0
      ) {
        prizeCount += 1;
      }
      if (
        prize["type" + index] === "exact match" &&
        this.gameSettingsService.winPattern.length !== 0 &&
        this.gameSettingsService.winPattern.filter(
          (winPat) => !_.isEqual(winPat, {})
        ).length !== 0 &&
        this.gameSettingsService.winPattern[index] &&
        this.gameSettingsService.winPattern[index].cashPrizeValue !== ""
      ) {
        prizeCount += 1;
      }
      if (
        prize["type" + index] === "solo" &&
        this.gameSettingsService.autoSelectPrizes.length !== 0 &&
        this.gameSettingsService.autoSelectPrizes[index] &&
        this.gameSettingsService.autoSelectPrizes[index].title !== ""
      ) {
        prizeCount += 1;
      }
    });
    return prizeCount;
  }

  gameCreated = async () => {
    const prizeCount = this.getNumberOfPrizes();
    this.fuseSplashScreen.show();

    // * uploading color wheel loader if file present
    const colorWheelType = this.gameSettingsService.colorWheel.type;
    const colorWheelData = this.gameSettingsService.colorWheel.data;

    if (colorWheelType === "img") {
      if (colorWheelData && colorWheelData.img && colorWheelData.img.file) {
        await this.colorWheelUpload(colorWheelData.img.file);
      }
    }

    const drawDate = moment(this.verticalStepperStep1.value.drawDate)
      .seconds(0)
      .milliseconds(0)
      .toJSON();
    const createdAt = moment().seconds(0).milliseconds(0).utc();
    const data: any = {
      adminId: this.adminApi.getCurrentId(),
      title: this.verticalStepperStep1.value.title,
      confirmationText: this.verticalStepperStep1.value.confirmationText,
      value: this.verticalStepperStep1.value.value,
      categoryId: this.verticalStepperStep1.value.categoryId,
      logo: {
        image: this.gameLogo,
        isStretch: this.logoStretch,
      },
      gameMenuArt: {
        image: this.menuArt,
        imageScale: this.imageScale,
        menuArtOption: this.manuArtSizeNavigation,
      },
      powerBar: {
        image: this.invitesData.powerBar.image,
        audio: this.invitesData.powerBar.audio,
        isExpand: this.invitesData.powerBar.isExpand,
      },
      invites:
        this.invitesData.invites.filter(
          (el) => el.image !== "" || el.inviteImageUrl !== ""
        ).length !== 0
          ? this.invitesData.invites
          : [],
      inviteType: this.inviteType,
      isInviteImageArtGradient: this.invitesData.isInviteImageArtGradient,
      isProgressBarTransparent: this.invitesData.isProgressBarTransparent,
      isGameMenuToolbar: this.gameSettingsService.isGameMenuToolbar,
      drawDate: drawDate,
      sharingContentCountdown:
        this.verticalStepperStep1.value.sharingContentCountdown !== ""
          ? this.verticalStepperStep1.value.sharingContentCountdown
          : null,
      schedule: this.verticalStepperStep1.value.schedule,
      timeDifference: moment(drawDate).diff(moment(createdAt), "seconds"),
      isRestartable: this.verticalStepperStep1.value.schedule ? true : false,
      isRegistration: this.verticalStepperStep1.value.isRegistration,
      isLocation: this.verticalStepperStep1.value.isLocation,
      isLocationProximity: this.verticalStepperStep1.value.isLocation
        ? this.verticalStepperStep1.value.isLocationProximity
        : false,
      prizeMode: this.game ? this.game.prizeMode : this.verticalStepperStep5.value.prizeMode,
      isPrivate: this.verticalStepperStep1.value.isPrivate,
      selectedPlayers:
        (this.game && this.game.selectedPlayers && this.game.selectedPlayers.length !== 0)
          ? this.game.selectedPlayers.map((el) => (el.id ? el.id : el))
          : (this.verticalStepperStep1.value.selectedPlayers
            ? this.verticalStepperStep1.value.selectedPlayers.map((el) => (el.id ? el.id : el))
            : []),
      selectedContent: this.game ? this.game.selectedContent : null,
      isRounded: this.verticalStepperStep2.value.isRounded,
      submittedEmojiSmallFormat: this.verticalStepperStep2.value.submittedEmojiSmallFormat,
      gameplayEmojiPosition: this.verticalStepperStep2.value.gameplayEmojiPosition,
      gamePlayEmojiBackground: this.verticalStepperStep2.value.gamePlayEmojiBackground,
      isFill: this.verticalStepperStep2.value.isFill,
      isCompressed: this.verticalStepperStep2.value.isCompressed,
      isSwipeMenu: this.verticalStepperStep5.value.isSwipeMenu,
      colorWheel: this.getColorWheelData(),
      tieBreakerColorWheel: this.gameSettingsService.tieBreakerColorWheel,
      surveyOnly: this.verticalStepperStep5.value.surveyOnly
    };

    if (this.dummyAssetsData.header && this.dummyAssetsData.header.image) {
      data.gameWinLosePreview = {
        header: this.dummyAssetsData.header,
      };
    }

    // Add prizeBackground if it has an image
    if (this.dummyAssetsData.prizeBackground && this.dummyAssetsData.prizeBackground.image) {
      if (!data.gameWinLosePreview) {
        data.gameWinLosePreview = {};
      }
      data.gameWinLosePreview.prizeBackground = this.dummyAssetsData.prizeBackground;
    }

    // Add buttonBackground if opacity is set
    if (this.dummyAssetsData.buttonBackground && this.dummyAssetsData.buttonBackground.opacity !== undefined) {
      if (!data.gameWinLosePreview) {
        data.gameWinLosePreview = {};
      }
      data.gameWinLosePreview.buttonBackground = this.dummyAssetsData.buttonBackground;
    }

    if (this.gameTicket && this.gameTicket.image) {
      data.gameTicket = this.gameTicket;
    }

    if (data.isRegistration) {
      data.registrationForm = this.game && this.game.registrationForm ? this.game.registrationForm : null;
    }

    if (this.layer) {
      data.gameMenuLayer = {
        image: this.layer,
        imageScale: this.layerImageScale,
        showInGameplay: this.showInGameplay,
      };
    }

    if (this.gameSettingsService.selectedSimilarGames.length !== 0) {
      data.similarGames = [...this.gameSettingsService.selectedSimilarGames];
    } else {
      data.similarGames = [];
    }

    if (data.isLocation) {
      data.notifyLocation = {
        lat: this.gameSettingsService.latitude,
        lng: this.gameSettingsService.longitude,
      };
      data.locationRadius = this.gameSettingsService.radius;
      data.locationRadiusUnit = this.gameSettingsService.unit;
    } else {
      data.notifyLocation = {
        lat: 0,
        lng: 0,
      };
      data.locationRadius = null;
      data.locationRadiusUnit = null;
    }
    if (this.inviteType === "color-gradient" || this.inviteType === "layer") {
      data.gameMenuArt.firstColor = this.menuArt1stColor;
      data.gameMenuArt.secondColor = this.menuArt2ndColor;
    }

    data.restartThreshold =
      data.schedule !== "none"
        ? this.verticalStepperStep1.value.restartThreshold
        : null;

    data.cycles =
      data.schedule !== "instant" && data.schedule !== "none"
        ? this.verticalStepperStep1.value.cycles
        : null;

    if (this.rule === "text") {
      data.textRules = this.verticalStepperStep1.value.textRules;
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.embededVideoRules = "";
      data.imageRule = {};
      data.embedWithTextContent = null;
      data.imageWithTextContent = null;
    } else if (this.rule === "embed") {
      data.embededVideoRules =
        this.verticalStepperStep1.value.embededVideoRules;
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {};
      data.embedWithTextContent = null;
      data.imageWithTextContent = null;
    } else if (this.rule === "image") {
      data.embededVideoRules = "";
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {
        img: this.verticalStepperStep1.value.imageRule
          ? this.verticalStepperStep1.value.imageRule.replace(this.s3BucketUrl, "")
          : "",
        isStretch: this.imageRuleStretch,
      };
      data.embedWithTextContent = null;
      data.imageWithTextContent = null;
    } else if (this.rule === "embedWithText") {
      data.embededVideoRules = "";
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {};
      const embedContent = this.verticalStepperStep1.value.embedWithTextContent || {};
      data.embedWithTextContent = {
        embededVideoRules: this.verticalStepperStep1.value.embededVideoRules || "",
        original: embedContent.original || "",
        translations: embedContent.translations || {},
      };
      data.imageWithTextContent = null;
    } else if (this.rule === "imageWithText") {
      data.embededVideoRules = "";
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {};
      const imgContent = this.verticalStepperStep1.value.imageWithTextContent || {};
      let rawImg = this.verticalStepperStep1.value.imageRule || "";
      if (rawImg) {
        rawImg = rawImg.replace(this.s3BucketUrl, "");
      }
      data.imageWithTextContent = {
        img: rawImg,
        isStretch: this.imageRuleStretch,
        original: imgContent.original || "",
        translations: imgContent.translations || {},
      };
      data.embedWithTextContent = null;
    } else if (this.rule === "") {
      data.embededVideoRules = "";
      data.ruleTextRange = this.verticalStepperStep1.value.ruleTextRange;
      data.textRules = "";
      data.imageRule = {};
      data.embedWithTextContent = null;
      data.imageWithTextContent = null;
    }

    if (this.automatchButtonArt) {
      const automatchUrl = this.automatchButtonArt.split("/");
      if (automatchUrl[0] === "https:") {
        this.automatchButtonArt = this.automatchButtonArt.replace(environment.s3BucketUrl, "");
      }
      data.automatchButtonArt = {
        image: this.automatchButtonArt
      };
    } else {
      data.automatchButtonArt = null;
    }

    if (this.selfPickButtonArt) {
      const selfPickUrl = this.selfPickButtonArt.split("/");
      if (selfPickUrl[0] === "https:") {
        this.selfPickButtonArt = this.selfPickButtonArt.replace(environment.s3BucketUrl, "");
      }
      data.selfPickButtonArt = {
        image: this.selfPickButtonArt
      };
    } else {
      data.selfPickButtonArt = null;
    }

    // FINAL X p28-36. See identical block in gameUpdate() for why these are
    // always sent rather than only when gameFormat === 'dressUp'.
    if (!this.syncDressUpGroupsFromScreens()) return;
    data.gameFormat = this.verticalStepperStep1.value.gameFormat || "standard";
    data.dressUpGroups = this.dressUpGroups;
    if (this.dressUpFigure) {
      const figureUrl = this.dressUpFigure.split("/");
      if (figureUrl[0] === "https:") {
        this.dressUpFigure = this.dressUpFigure.replace(environment.s3BucketUrl, "");
      }
      data.dressUpFigure = { image: this.dressUpFigure };
    } else {
      data.dressUpFigure = null;
    }

    data.titleOpt = this.titleOpt;
    data.shadowOpt = this.shadowOpt;
    data.categoryOpt = this.categoryOpt;
    data.enterFreeOpt = this.enterFreeOpt;
    data.seatMapOpt = this.seatMapOpt;

    data.grid =
      this.gridType === "4"
        ? { noOfColoumns: "4", itemsinGrid: "16", noOfGrids: "3" }
        : this.gridType === "2"
          ? { noOfColoumns: "2", itemsinGrid: "6", noOfGrids: "8" }
          : { noOfColoumns: "6", itemsinGrid: "48", noOfGrids: "1" };
    if (data.isPrivate && data.selectedPlayers.length === 0) {
      this.fuseSplashScreen.hide();
      this.snackBar.open("Please Select one or more Players", "ERROR", {
        verticalPosition: "top",
        duration: 3000,
      });
    } else if (!this.gridType) {
      this.snackBar.open("Please Select Grid Type", "ERROR", {
        verticalPosition: "top",
        duration: 3000,
      });
    } else if (data.prizeMode === "auto select mode" && prizeCount < 1) {
      this.fuseSplashScreen.hide();
      this.snackBar.open("Please Select at least one Prize", "ERROR", {
        verticalPosition: "top",
        duration: 3000,
      });
    } else if (data.prizeMode !== "auto select mode" && prizeCount < 2) {
      this.fuseSplashScreen.hide();
      this.snackBar.open("Please Select at least 2 prizes", "ERROR", {
        verticalPosition: "top",
        duration: 3000,
      });
    } else {
      let isGameCreated = true;
      if (data.prizeMode !== "auto select mode" && prizeCount > 1) {
        const prizeFormArray = this.prizeForm.getRawValue();
        prizeFormArray.prize.forEach((prize, index) => {
          if (
            prize["type" + index] === "exact match" &&
            prize["cashPrizeValue" + index] === ""
          ) {
            this.snackBar.open(
              "Exact match value is mandatory. Please fill the required field",
              "ERROR",
              {
                verticalPosition: "top",
                duration: 3000,
              }
            );
            isGameCreated = false;
            this.fuseSplashScreen.hide();
          }
        });
      }
      if (this.game && this.game.prizeMode === "auto select mode" && prizeCount > 0) {
        const prizeFormArray = this.prizeForm.getRawValue();
        prizeFormArray.prize.forEach((prize, index) => {
          if (
            prize["type" + index] === "exact match" &&
            prize["cashPrizeValue" + index] === ""
          ) {
            this.snackBar.open(
              "Exact match value is mandatory. Please fill the required field",
              "ERROR",
              {
                verticalPosition: "top",
                duration: 3000,
              }
            );
            isGameCreated = false;
            this.fuseSplashScreen.hide();
          }
        });
      }
      if (this.exactMatchCheck.filter((exact) => !exact).length !== 0) {
        isGameCreated = false;
        this.fuseSplashScreen.hide();
        alert("exact match must have 6 emojis");
      }
      if (this.gridType === "6") {
        this.gameSettingsService.gameEmojis = this.gameSettingsService.populated
          ? [...(this.gameSettingsService.gameEmojis || [])]
          : (this.game && this.game.emojis ? [...this.game.emojis] : [...(this.gameSettingsService.gameEmojis || [])]);
      }

      // console.log('creating game data', data);

      const updatedRewards = await this.gatherAndUploadRewards();

      if (isGameCreated) {
        this.mergingPopUpAndEmojis();

        let prizes = this.gatheringPrizeData("data");

        prizes = _.cloneDeep(this.transformingPrizeBeforeSaving(prizes));

        const emojisToSave =
          this.game && this.game.emojis
            ? this.game.emojis
            : this.gameSettingsService.gameEmojis || [];

        await this.upsertGame(data, emojisToSave, prizes, updatedRewards || [], "create");
        this.gameSettingsService.populated = false;
        this.gameSettingsService.extraEmojisSet = [];
        this.fuseSplashScreen.hide();
        this.snackBar.open("Game created successfully", "SUCCESS", {
          verticalPosition: "top",
          duration: 3000,
        });
        this.gameSettingsService.deleteCookies();
        this.router.navigate(["/dashboard"]);
      }
    }
  };

  public shuffle(index) {
    if (this.gameSettingsService.winPattern[index].cashPrizeValue !== "") {
      this.prizeForm.value.prize[index]["cashPrizeValue" + index] =
        this.gameSettingsService.winPattern[index].cashPrizeValue;
    }

    if (!this.gameSettingsService.winPattern[index]) {
      this.gameSettingsService.winPattern[index] = {};
    }
    const winPat = this.gameSettingsService.winPattern[index];

    let emojisArray = [
      ...this.gameSettingsService.reorganizeEmojisAndConcatenate(
        this.editedGameId,
        this.gameSettingsService.extraEmojisSet
      ),
    ];

    if (
      this.gridType === "2" &&
      this.prizeForm.value.prize[index]["cashPrizeValue" + index]
    ) {
      if (
        this.editedGameId &&
        (this.gameSettingsService.winPattern[index] ||
          this.gameSettingsService.autoSelectPrizes[index])
      ) {
        this.gameSettingsService.winPattern[index].id =
          winPat && winPat.id
            ? winPat.id
            : this.gameSettingsService.autoSelectPrizes[index] &&
              this.gameSettingsService.autoSelectPrizes[index].id
              ? this.gameSettingsService.autoSelectPrizes[index].id
              : "";
      }

      this.gameSettingsService.winPattern[index].index = index;
      this.gameSettingsService.winPattern[index].type = "exact match";
      let shuffledEmojis = shuffle.pick(emojisArray, { picks: 6 });
      shuffledEmojis.forEach((el, index) => (el.index = index));
      this.gameSettingsService.winPattern[index].cashPrizeImages =
        shuffledEmojis;
      this.gameSettingsService.winPattern[index].cashPrizeValue =
        this.prizeForm.value.prize[index]["cashPrizeValue" + index];
    } else if (
      this.gridType === "4" &&
      this.prizeForm.value.prize[index]["cashPrizeValue" + index]
    ) {
      if (
        this.editedGameId &&
        (this.gameSettingsService.winPattern[index] ||
          this.gameSettingsService.autoSelectPrizes[index])
      ) {
        this.gameSettingsService.winPattern[index].id =
          winPat && winPat.id
            ? winPat.id
            : this.gameSettingsService.autoSelectPrizes[index] &&
              this.gameSettingsService.autoSelectPrizes[index].id
              ? this.gameSettingsService.autoSelectPrizes[index].id
              : "";
      }

      this.gameSettingsService.winPattern[index].index = index;
      this.gameSettingsService.winPattern[index].type = "exact match";
      let shuffledEmojis = shuffle.pick(emojisArray, { picks: 6 });
      shuffledEmojis.forEach((el, index) => (el.index = index));
      this.gameSettingsService.winPattern[index].cashPrizeImages =
        shuffledEmojis;
      this.gameSettingsService.winPattern[index].cashPrizeValue =
        this.prizeForm.value.prize[index]["cashPrizeValue" + index];
    } else if (
      this.gridType === "6" &&
      this.prizeForm.value.prize[index]["cashPrizeValue" + index]
    ) {
      if (emojisArray === undefined) emojisArray = [];
      if (
        this.editedGameId &&
        (this.gameSettingsService.winPattern[index] ||
          this.gameSettingsService.autoSelectPrizes[index])
      ) {
        this.gameSettingsService.winPattern[index].id =
          winPat && winPat.id
            ? winPat.id
            : this.gameSettingsService.autoSelectPrizes[index] &&
              this.gameSettingsService.autoSelectPrizes[index].id
              ? this.gameSettingsService.autoSelectPrizes[index].id
              : "";
      }

      this.gameSettingsService.winPattern[index].index = index;
      this.gameSettingsService.winPattern[index].type = "exact match";
      let shuffledEmojis = shuffle.pick(emojisArray, { picks: 6 });
      shuffledEmojis.forEach((el, index) => (el.index = index));
      this.gameSettingsService.winPattern[index].cashPrizeImages =
        emojisArray.length !== 48
          ? alert("Please keep 48 emojis in screen")
          : shuffledEmojis;
      this.gameSettingsService.winPattern[index].cashPrizeValue =
        this.prizeForm.value.prize[index]["cashPrizeValue" + index];
    } else {
      if (!this.editedGameId && winPat && winPat.id) {
        this.gameSettingsService.winPattern[index] = "";
      } else {
        this.gameSettingsService.winPattern[index].cashPrizeValue = "";
        this.gameSettingsService.winPattern[index].cashPrizeImages = [];
      }
    }
    // * setting isDuplication to true
    this.gameSettingsService.isDuplicationFlag = true;

    this.saveInLocalStorage("prizeForm", this.prizeForm.value.prize);
    this.saveInLocalStorage("prize", this.gatheringPrizeData("data"));
  }

  // * SFX button functionality
  shouldShowButton(index: number, tabs): boolean {
    const adType =
      this.tabForm.value.popUp[index + tabs * this.emojisPerScreen][
      "adType" + (index + tabs * this.emojisPerScreen)
      ];
    return adType !== "admob";
  }

  shouldHighlightButton(index: number, tabs): boolean {
    const sfxSound =
      this.tabForm.value.popUp[index + tabs * this.emojisPerScreen][
      "sfxSound" + (index + tabs * this.emojisPerScreen)
      ];
    return sfxSound !== "sfxSound";
  }

  getButtonTextColor(index: number, tabs): string {
    const sfxSound =
      this.tabForm.value.popUp[index + tabs * this.emojisPerScreen][
      "sfxSound" + (index + tabs * this.emojisPerScreen)
      ];
    return sfxSound !== "sfxSound" ? "black" : "accent";
  }
  // * SFX button functionality

  clickCount = 0;
  timer: any;
  addEmojiMatchModePrize(item) {
    // Increment the click count
    this.clickCount++;

    // If it's the first click, start a timer
    if (this.clickCount === 1) {
      this.timer = setTimeout(() => {
        // If there is no second click within the specified time (e.g., 300 milliseconds),
        // reset the click count and timer
        this.clickCount = 0;
      }, 300); // Adjust the time as needed
    }

    // If it's the second click within the specified time, treat it as a double-click
    if (this.clickCount === 2) {
      clearTimeout(this.timer); // Clear the timer
      this.clickCount = 0; // Reset the click count

      // Perform the actions you want for the double-click event
      this.handleDoubleClick(item);
    }
  }

  handleDoubleClick(item) {
    let emptyIndex = null;

    // Find the first available empty index with less than 6 cashPrizeImages
    for (let i = 0; i < this.gameSettingsService.winPattern.length; i++) {
      const el = this.gameSettingsService.winPattern[i];
      if (el) {
        if (!el.cashPrizeImages) {
          break; // Break the loop if `el.cashPrizeImages` does not exist
        }

        if (el.cashPrizeImages.length < 6) {
          emptyIndex = el.index;
          break;
        }
      }
    }

    // If no empty index is found, show screensFullDialog
    if (emptyIndex === null) {
      this.screensFullDialog();
    } else if (!this.gameSettingsService.winPattern[emptyIndex].cashPrizeValue) {
      // Block adding emojis before a prize value is set (same guard as the
      // live add paths in screen-emojis / drag-drop)
      this.snackBar.open("Please enter a prize value first", "OK", {
        duration: 2000,
      });
    } else {
      // Show confirmation dialog before adding the emoji
      this.confirmationDialogRef = this.dialog.open(
        ConfirmationDialogComponent,
        {
          data: {
            title: "Confirmation!",
            message:
              "Are you certain about adding this emoji to the vacant space on the match mode prize?",
          },
        }
      );

      this.confirmationDialogRef
        .afterClosed()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((result) => {
          if (result) {
            if (emptyIndex !== null) {
              let extraImagesDelete = this.deleteWinPatterns.find(
                (el) => el.winPatternIndex === emptyIndex
              );

              // Check if there are deleteIndex values
              if (extraImagesDelete.deleteIndex.length !== 0) {
                const deleteIndex = extraImagesDelete.deleteIndex[0];

                // Insert the item at deleteIndex position in cashPrizeImages array
                this.gameSettingsService.winPattern[
                  extraImagesDelete.winPatternIndex
                ].cashPrizeImages.splice(deleteIndex, 0, item);

                // Remove the processed deleteIndex
                extraImagesDelete.deleteIndex.shift();

                // If deleteIndex array becomes empty, remove the element from deleteWinPatterns
                if (extraImagesDelete.deleteIndex.length === 0) {
                  const deleteWinPatternsIndex =
                    this.deleteWinPatterns.findIndex(
                      (el) => el.winPatternIndex === emptyIndex
                    );
                  if (deleteWinPatternsIndex !== -1) {
                    this.deleteWinPatterns.splice(deleteWinPatternsIndex, 1);
                  }
                }
              }
            }
          }
        });
    }
  }

  screensFullDialog() {
    this.confirmationDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        isConfirmation: true,
        message:
          "Please remove an emoji from any any selected match mode prize before retrying!",
      },
    });
  }

  getPrizeIndex(index: number, prizePage: number): number {
    return index + (prizePage - 1) * 6;
  }

  getCategoryById() {
    if (!this.categories) return null;
    return this.categories.find(
      (el) => el.id === this.verticalStepperStep1.value.categoryId
    );
  }

  openMathEditor(index) {
    this.matchEditorDialogRef = this.dialog.open(MatchEditorComponent, {
      panelClass: "match-editor-dialog",
      height: "auto",
      width: "690px",
      data: {
        message: "Are you sure you want to Delete?",
        prizeIndex: index,
        s3BucketUrl: this.s3BucketUrl,
        cashPrizeImagesOptions: this.cashPrizeImagesOptions,
        extraImagesDelete: this.extraImagesDelete.bind(this),
        shuffle: this.shuffle.bind(this),
        checkExactOrEliminationInPrizes: this.checkExactOrEliminationInPrizes(),
        assignEmojisToInviteImageArt: this.assignEmojisToInviteImageArt(true),
      },
    });
    this.matchEditorDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (result) => {
        // category deleting on selecting yes from confirmation dialog
        if (result) {
          const index = result.prizeIndex;

          const prizeFormArray = this.prizeForm.getRawValue();
          prizeFormArray.prize[index]["cashPrizeValue" + index] =
            this.gameSettingsService.winPattern[index].cashPrizeValue;
          this.prizeForm.patchValue({ prize: prizeFormArray.prize });

          // Additional properties for specific confetti types
          const confettiConfig = this.gameSettingsService.prizeConfetti[index];

          if (confettiConfig.type === "color") {
            this.gameSettingsService.winPattern[index].prizeConfetti =
              confettiConfig.color;
          } else if (confettiConfig.type === "emoji") {
            this.gameSettingsService.winPattern[index].prizeConfetti =
              confettiConfig.emoji;
          } else {
            this.gameSettingsService.winPattern[index].prizeConfetti =
              confettiConfig.canvas;
          }

          if (
            this.gameSettingsService.winPattern[index] &&
            this.gameSettingsService.winPattern[index].image1 &&
            this.gameSettingsService.winPattern[index].image1.file
          ) {
            const file = this.gameSettingsService.winPattern[index].image1.file;
            const uploadResourcePath =
              environment.mainContainer + "/" + environment.resources;
            try {
              this.fuseSplashScreen.show();
              let response: any = await this.gameSettingsService
                .uploadImage(
                  file,
                  uploadResourcePath,
                  this.adminApi.getCurrentToken().id
                )
                .toPromise();
              this.gameSettingsService.winPattern[index].image1.image =
                response.result.files["file"][0]["providerResponse"].name;
              this.fuseSplashScreen.hide();
            } catch (error) {
              console.error("error...", error);
              this.fuseSplashScreen.hide();
            }
          }
        }
      });
  }

  async onTicketUpload(fileOrEvent: File | any) {
    let file: File;

    // Handle both File objects and events
    if (fileOrEvent instanceof File) {
      file = fileOrEvent;
    } else if (fileOrEvent && fileOrEvent.target && fileOrEvent.target.files) {
      file = fileOrEvent.target.files[0];
    } else {
      return;
    }

    if (!file) return;

    try {
      this.fuseSplashScreen.show();

      // Compress the image
      const compressed = await this.helperService.compressImage(file);

      // Upload to S3
      const uploadPath = this.gameSettingsService.inviteImageArtUploadPath;
      const response: any = await this.gameSettingsService
        .uploadImage(
          compressed.compressedBlob,
          uploadPath,
          this.adminApi.getCurrentToken().id
        )
        .toPromise();

      // Extract the image name from response
      const result = response.result || response;
      const files = result.files || result;
      const fileList = files["file"] || files;

      if (fileList && fileList[0]) {
        const providerResponse = fileList[0]["providerResponse"] || fileList[0];
        this.gameTicket.image = providerResponse.name;
      }

      this.fuseSplashScreen.hide();
    } catch (error) {
      console.error("Ticket upload failed:", error);
      this.fuseSplashScreen.hide();
    }
  }

  imageSlots = IMAGE_SLOTS;
  placeholderImage = PLACEHOLDER_IMAGE;

  // Non-re-entrant. Re-assigning src from inside the element's own error handler restarts
  // the fetch, so a fallback that cannot itself load re-fires `error` without bound.
  // Angular registers (error) via addEventListener, so `img.onerror = null` would not
  // detach it -- the guard has to live on the element. Comparing the src attribute means
  // we bail out only when the fallback itself is what just failed, so the guard also
  // re-arms when a new image is bound.
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.getAttribute('src') === this.placeholderImage) {
      return;
    }
    img.src = this.placeholderImage;
  }

  getPrizeType(i: number): string {
    return getPrizeType(this.prizeForm.value, i);
  }

  getImageSrc(i: number, slotKey: string): string {
    let src = this.placeholderImage;

    // 1. Try Form (Raw Value) - Most current edit state
    if (this.prizeForm) {
      const rawValue = this.prizeForm.getRawValue();
      if (rawValue.prize) {
        src = getPrizeImageSrc(
          rawValue.prize,
          i,
          slotKey,
          this.s3BucketUrl
        );
      }
    }

    // 2. Try Auto Select Prizes (Specific mode cache)
    if (src === this.placeholderImage && this.gameSettingsService.autoSelectPrizes) {
      src = getPrizeImageSrc(
        this.gameSettingsService.autoSelectPrizes,
        i,
        slotKey,
        this.s3BucketUrl
      );
    }

    // 3. Try Service Prizes
    if (src === this.placeholderImage && this.gameSettingsService.prizes) {
      src = getPrizeImageSrc(
        this.gameSettingsService.prizes,
        i,
        slotKey,
        this.s3BucketUrl
      );
    }

    // 4. Try Component Prizes
    if (src === this.placeholderImage && this.prizes) {
      src = getPrizeImageSrc(
        this.prizes,
        i,
        slotKey,
        this.s3BucketUrl
      );
    }

    // 5. Try Game Prizes (Model)
    if (src === this.placeholderImage && this.game && this.game.prizes) {
      src = getPrizeImageSrc(
        this.game.prizes,
        i,
        slotKey,
        this.s3BucketUrl
      );
    }

    return src;
  }

  getPrizeIdx(i: number, page?: number): number {
    return page != null ? this.getPrizeIndex(i, page) : i;
  }

  getPrizeTypeByIndex(index: number): string {
    if (!this.prizeForm) return '';
    const rawValue = this.prizeForm.getRawValue();
    const prize = rawValue.prize[index];
    if (!prize) return '';
    return prize['type' + index] || prize.type;
  }

  isAutoPrize(type: string): boolean {
    return type === 'auto select' || type === 'solo' || type === 'top 6';
  }

  isExactMatch(type: string): boolean {
    return type === 'exact match';
  }

  get prizesControls() {
    return (this.prizeForm.get('prize') as any).controls;
  }

  getPrizeAt(index: number) {
    return this.prizesControls[index];
  }

}
